import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { intelligencePath, parseDte, parseFamily, parseStrategy } from "@/workspace/handoff";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { RegimeEdgePanel, RegimeEdgeChipsForTicker } from "@/components/RegimeEdge";
import { RegimeSojournBar } from "@/components/RegimeSojournBar";
import { UOAPanel } from "@/components/UOAPanel";
import { IntelStrip } from "@/components/IntelStrip";
import { LayerProgress } from "@/components/LayerProgress";
import { DTETimeline, ExpiryChips } from "@/components/DTETimeline";
import { REGIME_DELTA_TARGET, regimeDeltaTarget, signalColor as sigColor, freshnessLabel as freshLabel } from "@/lib/tradeSignals";
import { verdictDisplay, ANALYTICS_DISCLAIMER, beyondVisibility, NO_VISIBILITY_LABEL, NO_VISIBILITY_COLOR, noVisibilityNote } from "@/lib/displayLabels";
import OrderTicketDialog from "@/components/OrderTicketDialog";
import ApiAuditChip from "@/components/ApiAuditChip";
import RegimeDiffChart from "@/components/RegimeDiffChart";
import { StatusBadge, statusToneStyle } from "@/components/wheel/StatusBadge";
import { statusSeverity, compactStatusLabel, primaryRowStatus, workbenchOptionStatus } from "@/lib/formatMarket";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, RefreshCw, TrendingUp, AlertTriangle, Shield, Zap, Target, Play, CheckCircle, Gauge, ChevronDown, Briefcase, Check, ArrowRight, ClipboardCheck, HelpCircle } from "lucide-react";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Area, AreaChart, LineChart,
  Cell
} from "recharts";

// Pure SVG Candlestick Chart with SMA overlays

const stateColors = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };

// HMM regime semantics — surfaced as tooltips so users don't read "Volatile" as
// "bearish". Volatile means HIGH VARIANCE / HIGH IV — direction-agnostic.
const REGIME_SEMANTICS = {
  "Quiet Range": "Low variance, mean-reverting. Low IV — short premium pays thin; long premium is cheap.",
  "Bullish Trend": "Sustained upward drift with positive momentum. Favours long-delta and put-side income.",
  "Bearish Trend": "Sustained downward drift. Favours call-side income and protective hedges.",
  "Volatile": "High variance / elevated IV — direction-agnostic. Premium is rich, but expected moves are large. Bullish-tilted Volatile can still favour BPS/CSP via the IV-crush tailwind.",
};

// Small inline help glyph that shows a native tooltip on hover. We avoid the
// shadcn <Tooltip> here because it needs a provider context and we want this
// to drop into any pill without wiring.
function RegimeHelp({ state }) {
  const txt = REGIME_SEMANTICS[state];
  if (!txt) return null;
  return (
    <span
      data-testid={`regime-help-${state?.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      title={`${state} — ${txt}`}
      className="ml-1 inline-flex items-center justify-center cursor-help select-none"
      style={{ color: "var(--hoip-text-tertiary)", opacity: 0.55 }}
      aria-label={`What does ${state} mean?`}
    >
      <HelpCircle className="w-2.5 h-2.5" />
    </span>
  );
}


// ═══ Badge count + sort helper ═══
function getBadgeCount(s) {
  let count = 0;
  if (s.earn_bonus) count++;            // ★ EARN
  if (s.complements_position) count++;  // equity+
  if (s.wheel_compatible) count++;      // Wheel
  if (s.bt_promoted) count++;           // ★
  if (s.regime_changed) count++;        // orange dot
  if (s.skew_badge) count++;            // Skew+
  return count;
}
// Audit fix note (Iter193): verdict tiers removed — flat ungated list.

// ═══ Iter190 — Strategy Family metadata + rating-view sorting ═══
const FAMILY_META = {
  premium_selling: {
    label: "Premium-Selling", color: "#14B8A6",
    tip: "Premium-Selling / High-POP — collects a credit repeatedly. High win rate is expected by design; the risk is tail losses. WR comparisons only make sense within this family.",
  },
  long_vol: {
    label: "Long-Vol", color: "#F59E0B",
    tip: "Long-Vol / Directional — pays a premium and needs a real move. Low win rate is expected and normal; occasional large winners must offset frequent small losses. Compare via Edge/PF, not WR.",
  },
  equity: {
    label: "Equity", color: "#94A3B8",
    tip: "Pure equity position — no option premium, theta, or IV exposure. Neither option-family lens (WR vs Edge) carries the same structural meaning here.",
  },
};

function FamilyChip({ family, strategyId }) {
  const meta = FAMILY_META[family];
  if (!meta) return null;
  return (
    <span data-testid={`family-chip-${strategyId}`}
      className="text-[8px] px-1 py-0.5 rounded-sm font-medium shrink-0"
      style={{ color: meta.color, border: `1px solid ${meta.color}55`, background: `${meta.color}0D` }}
      title={meta.tip}>
      {meta.label}
    </span>
  );
}

// Iter193 — flat ungated ranking. Default lens = Edge/PF (risk-adjusted:
// PF nets loss magnitude against win frequency, so uncapped-risk strategies
// can't top the list on raw WR alone). Insufficient samples sort last.
const RANK_VIEWS = [
  { id: "edge", label: "Edge / PF", sortedBy: "Highest Edge",
    note: "Sorted by expectancy per trade (with Profit Factor) — the risk-adjusted default, since it nets win rate against payoff size." },
  { id: "wr", label: "Win Rate", sortedBy: "Highest WR",
    note: "Sorted by simulated win rate — most meaningful within the Premium-Selling family. WR ignores loss magnitude: uncapped-risk strategies are tagged." },
  { id: "blended", label: "Blended Score", sortedBy: "Highest Blended Score",
    note: "Blended Score — combines Edge, Fit, and confidence into one number; a summary scan, not a recommendation." },
];

function rankSortStrats(strats, view) {
  const ins = (s) => (s.bt_insufficient || s.bt_win_rate == null) ? 1 : 0;
  return [...strats].sort((a, b) => {
    if (ins(a) !== ins(b)) return ins(a) - ins(b);
    if (view === "wr") {
      const aw = a.bt_win_rate, bw = b.bt_win_rate;
      if (aw == null && bw == null) return b.score - a.score;
      if (aw == null) return 1;
      if (bw == null) return -1;
      if (bw !== aw) return bw - aw;
      return b.score - a.score;
    }
    if (view === "blended") return b.score - a.score;
    // edge (default)
    const ae = a.bt_edge_pct, be = b.bt_edge_pct;
    if (ae == null && be == null) return b.score - a.score;
    if (ae == null) return 1;
    if (be == null) return -1;
    if (be !== ae) return be - ae;
    return (b.bt_profit_factor ?? 0) - (a.bt_profit_factor ?? 0);
  });
}

// ═══ Strategy Row with all badges ═══
function StrategyRow({ s, isSelected, onSelect, rankView }) {
  return (
    <div data-testid={`strategy-row-${s.strategy_id}`}
      onClick={onSelect}
      className="px-3 py-2 flex items-start gap-2 cursor-pointer transition-colors hover:bg-[var(--hoip-hover)]"
      style={isSelected ? { background: "rgba(59,130,246,0.08)", borderLeft: "2px solid #3B82F6" } : {}}>
      {s.bt_insufficient ? (
        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold mt-0.5 shrink-0"
          style={{ color: "var(--hoip-text-tertiary)", background: "var(--hoip-hover)", border: "1px dashed var(--hoip-border)" }}
          title={`Only n=${s.bt_sample_size ?? 0} historical samples for ${s.bt_regime || "the current regime"} — below the 20-sample reliability floor. Shown lower in the list; score reflects regime fit only.`}
          data-testid={`insufficient-sample-${s.strategy_id}`}>
          Insufficient Sample · n={s.bt_sample_size ?? 0}
        </span>
      ) : (
        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold mt-0.5 shrink-0 font-mono-data"
          style={{ color: "var(--hoip-text-secondary)", background: "var(--hoip-hover)" }}
          title={`n=${s.bt_sample_size ?? "—"} regime-conditioned daily entries (${s.bt_regime || "current regime"}, 2y) — the canonical population behind these numbers.`}
          data-testid={`sample-chip-${s.strategy_id}`}>
          n={s.bt_sample_size ?? "—"}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-medium" style={{ color: "var(--hoip-text-primary)" }}>{s.strategy_name}</span>
          <FamilyChip family={s.family} strategyId={s.strategy_id} />
          {s.risk_profile === "undefined" && (
            <span className="text-[8px] px-1 py-0.5 rounded-sm font-bold shrink-0"
              style={{ color: "#EF4444", border: "1px solid rgba(239,68,68,0.45)", background: "rgba(239,68,68,0.06)" }}
              title="Loss is not capped by structure (uncapped short exposure). Win rate alone never reflects loss magnitude for these strategies."
              data-testid={`unlimited-risk-${s.strategy_id}`}>
              Unlimited Risk
            </span>
          )}
          <span className="font-mono-data text-[9px] px-1 py-0.5 rounded-sm" style={{
            color: s.score >= 70 ? "#10B981" : s.score >= 45 ? "#F59E0B" : "#EF4444",
            background: s.score >= 70 ? "rgba(16,185,129,0.1)" : s.score >= 45 ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)"
          }}>{s.score}</span>
          {s.regime_changed && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" title="Regime recently changed" />}
          {s.bt_promoted && <span className="text-[9px] text-emerald-400" title="BT Promoted">&#9733;</span>}
          {s.earn_bonus && <span className="text-[8px] px-1 py-0.5 rounded-sm font-bold" style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B" }} data-testid="earn-bonus-badge">&#9733; EARN</span>}
          {s.complements_position && <span className="text-[8px] px-1 py-0.5 rounded-sm" style={{ background: "rgba(59,130,246,0.1)", color: "#3B82F6" }}>equity+</span>}
          {s.wheel_compatible && <span className="text-[8px] px-1 py-0.5 rounded-sm" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }}>Wheel</span>}
          {s.skew_badge && <span className="text-[8px] px-1 py-0.5 rounded-sm" style={{ background: "rgba(139,92,246,0.1)", color: "#8B5CF6" }}>Skew+</span>}
          {s.regime_downweight?.active && (
            <span
              data-testid={`downweight-badge-${s.strategy_id}`}
              className="text-[8px] px-1 py-0.5 rounded-sm font-bold flex items-center gap-1"
              style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B",
                       border: "1px solid rgba(245,158,11,0.35)" }}
              title={
                `Volatile 5d — ${s.strategy_id} score cut ` +
                `${s.regime_downweight.score_before} → ${s.regime_downweight.score_after} ` +
                `(×${s.regime_downweight.score_multiplier}).` +
                (s.regime_downweight.verdict_change
                  ? `\nVerdict: ${s.regime_downweight.verdict_change.from} → ${s.regime_downweight.verdict_change.to}.`
                  : "") +
                `\n5d smoothed=Volatile, Instant=${s.regime_downweight.state_instant || "—"}.` +
                `\nEvery cut is logged to db.ranker_haircut_events for audit.`
              }>
              ▼ Vol-5d
            </span>
          )}
          {s.lots_available != null && <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{s.lots_available} lot{s.lots_available !== 1 ? "s" : ""}</span>}
          {isSelected && <span className="text-[8px] ml-auto" style={{ color: "#3B82F6" }}>SELECTED</span>}
        </div>
        {s.score_axes && (
          <p className="text-[9px] mt-0.5 font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}
            title="Score = 55% Regime Fit + 30% Historical Edge (normalized expectancy: mean simulated P&L ÷ worst simulated loss) + 15% Evidence (sample size). Adjustment chips (earnings, Vol-5d) apply on top and are shown separately."
            data-testid={`score-axes-${s.strategy_id}`}>
            Fit {s.score_axes.fit} · Edge {s.bt_edge_pct != null ? `${s.bt_edge_pct >= 0 ? "+" : ""}${s.bt_edge_pct}%/trade` : "—"} · n={s.bt_sample_size ?? "—"} over 2y
          </p>
        )}
        {s.bt_win_rate != null && !s.bt_insufficient && (
          <p className="text-[9px] mt-0.5 font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}
            title="Simulated over 2y of daily closes, conditioned on the current regime, with BS-priced regime-scaled strikes — not real chain prices. Entries overlap (one per trading day). Historical simulation, not a forecast."
            data-testid={`bt-detail-${s.strategy_id}`}>
            WR {s.bt_win_rate}%{rankView === "wr" && s.risk_profile === "undefined" && <span className="font-bold" style={{ color: "#EF4444" }}> (Unlimited Risk)</span>} · Edge {s.bt_edge_pct != null ? `${s.bt_edge_pct >= 0 ? "+" : ""}${s.bt_edge_pct}%` : "—"}
          </p>
        )}
        {rankView === "wr" && s.family === "long_vol" && (
          <p className="text-[9px] mt-0.5 italic" style={{ color: "var(--hoip-text-tertiary)" }}
            data-testid={`longvol-wr-note-${s.strategy_id}`}>
            Low WR is structurally expected for long-vol strategies — see Edge/PF view.
          </p>
        )}
        <p className="text-[9px] mt-0.5 line-clamp-1" style={{ color: "var(--hoip-text-tertiary)" }}>{s.rationale}</p>
        {s.cc_gain_note && <p className="text-[9px] mt-0.5" style={{ color: "#F59E0B" }} data-testid="cc-gain-note">{s.cc_gain_note}</p>}
      </div>
    </div>
  );
}

// ═══ Position Intelligence — Mode A (has position) + Mode B (new entry) ═══
function PositionIntelligence({ symbol, positionContext, rec, onSelectStrategy, onUserSelectStrategy, selectedStrategy, selectedExpiry, onRegimeLoaded }) {
  const [ranked, setRanked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMoreGo, setShowMoreGo] = useState(false);
  const [rankView, setRankView] = useState(() => sessionStorage.getItem("ml_rank_view") || "edge");
  const changeRankView = (v) => { setRankView(v); sessionStorage.setItem("ml_rank_view", v); };
  const [searchParams] = useSearchParams();
  const [familyFilter, setFamilyFilter] = useState(() => (
    parseFamily(searchParams.get("family")) || sessionStorage.getItem("ml_family_filter") || "all"
  ));
  const changeFamilyFilter = (f) => { setFamilyFilter(f); sessionStorage.setItem("ml_family_filter", f); };
  useEffect(() => {
    const fromUrl = parseFamily(searchParams.get("family"));
    if (fromUrl) {
      setFamilyFilter(fromUrl);
      sessionStorage.setItem("ml_family_filter", fromUrl);
    }
  }, [searchParams]);
  const [showUndefRisk, setShowUndefRisk] = useState(() => sessionStorage.getItem("ml_show_undef_risk") === "1");
  const changeShowUndefRisk = (v) => { setShowUndefRisk(v); sessionStorage.setItem("ml_show_undef_risk", v ? "1" : "0"); };

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    const expiryParam = selectedExpiry ? `&expiry=${selectedExpiry}` : '';
    api.get(`/regime/ranked-strategies/${symbol}?dte=30${expiryParam}`)
      .then(({ data }) => {
        setRanked(data);
        if (onRegimeLoaded && data?.regime) onRegimeLoaded(data.regime);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [symbol, selectedExpiry]);

  // Auto-select top of the default Edge/PF-sorted flat list (defined-risk, reliable sample first)
  useEffect(() => {
    if (!ranked) return;
    const allStrats = ranked.ranked_strategies || [];
    const sorted = rankSortStrats(allStrats.filter(s => s.risk_profile !== "undefined"), "edge");
    if (sorted.length > 0 && onSelectStrategy) {
      const top = sorted[0].strategy_id;
      if (!selectedStrategy || !sorted.find(s => s.strategy_id === selectedStrategy)) {
        onSelectStrategy(top);
      }
    }
  }, [ranked]);

  if (loading) return (
    <div className="border rounded-sm p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
      <div className="flex items-center gap-2 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        <RefreshCw className="w-3 h-3 animate-spin" /> Loading strategy intelligence...
      </div>
    </div>
  );

  if (error || !ranked) return null;

  const pos = ranked.position;
  const regime = ranked.regime;
  const isNewEntry = ranked.mode === "B";
  const allStrats = ranked.ranked_strategies || [];

  // Iter193 — flat ungated list: family filter + undefined-risk opt-in, sorted by selected lens
  const famMatch = (s) => familyFilter === "all" || s.family === familyFilter;
  const riskMatch = (s) => showUndefRisk || s.risk_profile !== "undefined";
  const goStrats = rankSortStrats(allStrats.filter(s => famMatch(s) && riskMatch(s)), rankView);
  const hiddenUndefCount = showUndefRisk ? 0 : allStrats.filter(s => famMatch(s) && s.risk_profile === "undefined").length;

  const TOP_VISIBLE = 3;
  const topStrats = goStrats.slice(0, TOP_VISIBLE);
  const restStrats = goStrats.slice(TOP_VISIBLE);

  return (
    <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid="position-intelligence">
      {/* Header */}
      <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: "var(--hoip-border)" }}>
        <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--hoip-text-primary)" }}>
          <Shield className="w-3.5 h-3.5 text-blue-500" />
          <span>Position Intelligence</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-sm ml-1" style={{ background: "rgba(59,130,246,0.1)", color: "#3B82F6" }}>
            {isNewEntry ? "New Entry" : "Portfolio"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* HMM 5d smoothed — PRIMARY driver per HMM_HORIZON_SPEC.
              Falls back to the instantaneous state when horizons unavailable. */}
          {regime?.horizons?.smoothed_5d?.state ? (
            <span data-testid="pi-regime-5d"
              className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data flex items-center"
              style={{
                color: stateColors[regime.horizons.smoothed_5d.state],
                background: `${stateColors[regime.horizons.smoothed_5d.state]}15`,
              }}
              title="HMM 5d smoothed regime — the primary driver for strategy suggestions. Survives one bar of noise.">
              HMM 5d: {regime.horizons.smoothed_5d.state}
              <RegimeHelp state={regime.horizons.smoothed_5d.state} />
            </span>
          ) : (
            <span data-testid="pi-regime-raw" className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data flex items-center" style={{
              color: stateColors[regime?.state], background: `${stateColors[regime?.state]}15`
            }}>
              {regime?.state} {(regime?.prob * 100)?.toFixed(0)}%
              <RegimeHelp state={regime?.state} />
            </span>
          )}
          {/* Run-length chip — supporting context for the 5d chip. Iter128
              scoped this tightly: single chip only, not a new UI block. A
              5d regime that flipped 2 bars ago carries different confidence
              than one that's held 15 bars, even at the same stated prob. */}
          {regime?.horizons?.smoothed_5d?.bars_held > 0 && (
            <span data-testid="pi-regime-runlength"
              className="text-[9px] px-1.5 py-0.5 rounded-sm font-mono-data"
              style={{
                color: "var(--hoip-text-secondary)",
                background: "var(--hoip-card-alt, rgba(113,113,122,0.10))",
                border: "1px solid var(--hoip-border)",
              }}
              title={`5d ${regime.horizons.smoothed_5d.state} has held for ${regime.horizons.smoothed_5d.bars_held} bar${regime.horizons.smoothed_5d.bars_held === 1 ? "" : "s"}${regime.horizons.smoothed_5d.first_bar_date ? ` (since ${regime.horizons.smoothed_5d.first_bar_date})` : ""}. Longer runs = higher stability; fresh flips carry entry risk even at the same stated probability.`}>
              · {regime.horizons.smoothed_5d.bars_held}{regime.horizons.smoothed_5d.run_length_capped ? "+" : ""} bar{regime.horizons.smoothed_5d.bars_held === 1 ? "" : "s"}
            </span>
          )}
          {/* HMM Instant chip — secondary context, shown alongside 5d so the
              catalyst-window flip is visible without driving the ranking. */}
          {regime?.horizons?.instant?.state && (
            <span data-testid="pi-regime-instant"
              className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data flex items-center"
              style={{
                color: stateColors[regime.horizons.instant.state],
                background: `${stateColors[regime.horizons.instant.state]}12`,
                opacity: 0.85,
              }}
              title="HMM Instant regime + confidence — event-window awareness only. Cannot promote/demote the ranked strategy list.">
              Instant: {regime.horizons.instant.state} {Math.min(99, regime.horizons.instant.confidence ?? 0).toFixed(0)}% confidence
            </span>
          )}
          {/* Wide-margin disagreement caution — spec addendum. */}
          {regime?.horizons?.disagreement?.wide_margin && (
            <span data-testid="pi-regime-disagreement"
              className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data flex items-center gap-1"
              style={{
                color: "#F59E0B",
                background: "rgba(245,158,11,0.12)",
                border: "1px solid rgba(245,158,11,0.35)",
              }}
              title="Instant and 5d smoothed regimes disagree at high confidence. Treat entries with caution regardless of scheduled catalysts.">
              <AlertTriangle className="w-2.5 h-2.5" />
              5d vs Instant disagree
            </span>
          )}
          {regime?.confirmed_state && !regime?.horizons && regime.confirmed_state !== regime.state && (
            <span data-testid="pi-regime-confirmed"
              className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data"
              style={{
                color: stateColors[regime.confirmed_state] || "var(--hoip-text-tertiary)",
                background: `${stateColors[regime.confirmed_state] || "#71717A"}15`,
              }}
              title="Confirmed state — majority of the last 5 bars. Strategy gating uses this, not the raw daily flip.">
              {regime.confirmed_state} <span className="font-normal opacity-70">confirmed</span>
            </span>
          )}
          {regime?.label_override && regime?.raw_state && regime.raw_state !== regime.state && (
            <span data-testid="pi-regime-override"
              className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold font-mono-data flex items-center gap-1"
              style={{
                color: "#EAB308",
                background: "rgba(234,179,8,0.12)",
                border: "1px solid rgba(234,179,8,0.35)",
              }}
              title={regime.label_override}>
              <AlertTriangle className="w-2.5 h-2.5" />
              was {regime.raw_state}
            </span>
          )}
        </div>
      </div>

      {/* Position summary (Mode A only) */}
      {!isNewEntry && pos?.holds_stock && (
        <div className="px-3 py-2 border-b text-[10px] flex flex-wrap gap-x-4 gap-y-0.5" style={{ borderColor: "var(--hoip-border)" }}>
          <span style={{ color: "var(--hoip-text-secondary)" }}>
            <span className="font-mono-data font-medium">{pos.total_shares}</span> shares ({pos.lots} lot{pos.lots !== 1 ? "s" : ""})
          </span>
          {pos.avg_cost > 0 && <span style={{ color: "var(--hoip-text-tertiary)" }}>Avg: <span className="font-mono-data">${pos.avg_cost}</span></span>}
          {pos.unrealized_pnl !== 0 && (
            <span style={{ color: pos.unrealized_pnl > 0 ? "#10B981" : "#EF4444" }}>
              P&L: <span className="font-mono-data">{pos.unrealized_pnl > 0 ? "+" : ""}${pos.unrealized_pnl.toLocaleString()}</span>
            </span>
          )}
        </div>
      )}

      {/* Earnings banner — past vs future */}
      {ranked.earnings?.earnings_passed ? (
        <div className="px-3 py-1.5 border-b text-[10px] flex items-center gap-1.5"
          style={{ borderColor: "var(--hoip-border)", background: "rgba(16,185,129,0.06)", color: "#10B981" }}
          data-testid="earnings-passed-banner">
          <Zap className="w-3 h-3" />
          Earnings just reported ({ranked.earnings.next_date}) — IV likely deflating. Post-earnings premium environment.
          {ranked.earnings.next_expected_earnings && <span className="ml-1 opacity-70">Next earnings expected {ranked.earnings.next_expected_earnings}.</span>}
        </div>
      ) : ranked.earnings?.days_to_earnings != null && ranked.earnings.days_to_earnings === 0 ? (
        <div className="px-3 py-1.5 border-b text-[10px] flex items-center gap-1.5"
          style={{ borderColor: "var(--hoip-border)", background: "rgba(245,158,11,0.06)", color: "#f59e0b" }}
          data-testid="earnings-today-banner">
          <AlertTriangle className="w-3 h-3" />
          Earnings today ({ranked.earnings.next_date}) — expect elevated IV and gap risk into close.
        </div>
      ) : ranked.earnings?.days_to_earnings != null && ranked.earnings.days_to_earnings > 0 && ranked.earnings.days_to_earnings <= 30 ? (
        <div className="px-3 py-1.5 border-b text-[10px] flex items-center gap-1.5"
          style={{ borderColor: "var(--hoip-border)", background: "rgba(245,158,11,0.06)", color: "#f59e0b" }}
          data-testid="earnings-upcoming-banner">
          <AlertTriangle className="w-3 h-3" />
          Earnings in {ranked.earnings.days_to_earnings}d ({ranked.earnings.next_date})
          {ranked.earnings.days_to_earnings <= 7 && " — premium-selling strategies have historically shown wider IV-crush variance within this window (see Backtest Lab)"}
        </div>
      ) : null}

      {/* Portfolio Actions — position management cards */}
      {ranked.portfolio_actions && (
        <div className="border-b" style={{ borderColor: "var(--hoip-border)" }} data-testid="portfolio-actions">
          <div className="px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-bold"
            style={{ background: "rgba(59,130,246,0.06)", color: "#3B82F6" }}>
            <Briefcase className="w-3 h-3" />
            Portfolio Actions — for your {ranked.portfolio_actions.shares} shares
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-x" style={{ borderColor: "var(--hoip-divider)" }}>
            {/* Income */}
            {ranked.portfolio_actions.income && (
              <div className="px-3 py-2 cursor-pointer hover:bg-[var(--hoip-hover)]"
                onClick={() => onSelectStrategy && onSelectStrategy(ranked.portfolio_actions.income.strategy_id)}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded-sm" style={{ background: "rgba(16,185,129,0.1)", color: "#10B981" }}>INCOME</span>
                  <span className="text-[10px] font-medium" style={{ color: "var(--hoip-text-primary)" }}>{ranked.portfolio_actions.income.strategy}</span>
                </div>
                <p className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{ranked.portfolio_actions.income.rationale}</p>
              </div>
            )}
            {/* Protection */}
            {ranked.portfolio_actions.protection && (
              <div className="px-3 py-2 cursor-pointer hover:bg-[var(--hoip-hover)]"
                onClick={() => onSelectStrategy && onSelectStrategy(ranked.portfolio_actions.protection.strategy_id)}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded-sm" style={{ background: "rgba(59,130,246,0.1)", color: "#3B82F6" }}>PROTECT</span>
                  <span className="text-[10px] font-medium" style={{ color: "var(--hoip-text-primary)" }}>{ranked.portfolio_actions.protection.strategy}</span>
                </div>
                <p className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{ranked.portfolio_actions.protection.rationale}</p>
              </div>
            )}
            {/* Exit Signal */}
            {ranked.portfolio_actions.exit && (
              <div className="px-3 py-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded-sm" style={{
                    background: ranked.portfolio_actions.exit.signal === "WARNING" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)",
                    color: ranked.portfolio_actions.exit.signal === "WARNING" ? "#EF4444" : "#F59E0B"
                  }}>{ranked.portfolio_actions.exit.signal}</span>
                  <span className="text-[10px] font-medium" style={{ color: "var(--hoip-text-primary)" }}>Exit Signal</span>
                </div>
                <p className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{ranked.portfolio_actions.exit.rationale}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 1: Regime-Ranked Strategies — top 3 visible, rest collapsed */}
      {allStrats.length > 0 && (
        <div data-testid="go-strategies-section">
          <div className="px-3 py-1.5 border-b flex items-center justify-between gap-2 flex-wrap"
            style={{ background: "rgba(16,185,129,0.06)", borderColor: "var(--hoip-border)" }}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold" style={{ color: "#10B981" }}>
              <CheckCircle className="w-3 h-3" />
              Regime-Ranked Strategies ({goStrats.length})
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center rounded-sm overflow-hidden border" style={{ borderColor: "var(--hoip-border)" }} data-testid="family-filter">
                {[["all", "All", "#3B82F6"], ["premium_selling", "Premium-Selling", FAMILY_META.premium_selling.color], ["long_vol", "Long-Vol", FAMILY_META.long_vol.color], ["equity", "Equity", FAMILY_META.equity.color]].map(([id, label, color]) => (
                  <button key={id} onClick={() => changeFamilyFilter(id)} data-testid={`family-filter-${id}`}
                    className="text-[9px] px-2 py-1 cursor-pointer transition-colors font-medium"
                    style={familyFilter === id
                      ? { background: `${color}22`, color }
                      : { background: "transparent", color: "var(--hoip-text-tertiary)" }}
                    title={id === "all" ? "Show all strategy families" : FAMILY_META[id]?.tip}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex items-center rounded-sm overflow-hidden border" style={{ borderColor: "var(--hoip-border)" }} data-testid="rank-view-toggle">
              {RANK_VIEWS.map(v => (
                <button key={v.id} onClick={() => changeRankView(v.id)} data-testid={`rank-view-${v.id}`}
                  className="text-[9px] px-2 py-1 cursor-pointer transition-colors font-medium"
                  style={rankView === v.id
                    ? { background: "rgba(59,130,246,0.15)", color: "#3B82F6" }
                    : { background: "transparent", color: "var(--hoip-text-tertiary)" }}
                  title={v.note}>
                  {v.label}
                </button>
              ))}
              </div>
              <label className="flex items-center gap-1 text-[9px] cursor-pointer select-none" data-testid="undef-risk-toggle"
                style={{ color: "var(--hoip-text-tertiary)" }}
                title="Off by default: strategies whose loss is not capped by structure (Naked Call, Naked Put, Short Straddle/Strangle, Short Stock) are excluded from the ranked view unless enabled.">
                <input type="checkbox" checked={showUndefRisk} onChange={(e) => changeShowUndefRisk(e.target.checked)}
                  className="w-3 h-3 accent-red-500" data-testid="undef-risk-checkbox" />
                Show undefined-risk strategies
              </label>
            </div>
          </div>
          <div className="px-3 py-1 border-b text-[9px] flex items-center gap-1.5"
            style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
            data-testid="rank-view-note">
            <span className="font-medium shrink-0" style={{ color: "var(--hoip-text-secondary)" }}>
              Sorted by: {RANK_VIEWS.find(v => v.id === rankView)?.sortedBy}
            </span>
            <span className="line-clamp-1">— {RANK_VIEWS.find(v => v.id === rankView)?.note}</span>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--hoip-divider)" }}>
            {goStrats.length === 0 && (
              <div className="px-3 py-2 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="family-filter-empty">
                No {FAMILY_META[familyFilter]?.label || ""} strategies match the current filters — switch family or enable undefined-risk strategies.
              </div>
            )}
            {topStrats.map((s) => <StrategyRow key={s.strategy_id} s={s} isSelected={selectedStrategy === s.strategy_id}
              onSelect={() => (onUserSelectStrategy || onSelectStrategy)?.(s.strategy_id)} rankView={rankView} />)}
          </div>
          {restStrats.length > 0 && (
            <>
              <button onClick={() => setShowMoreGo(!showMoreGo)} data-testid="toggle-more-strategies"
                className="w-full px-3 py-1.5 border-b border-t flex items-center justify-center gap-1.5 text-[10px] cursor-pointer transition-colors hover:bg-[var(--hoip-hover)]"
                style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
                <ChevronDown className={`w-3 h-3 transition-transform ${showMoreGo ? "rotate-180" : ""}`} />
                {showMoreGo ? "Hide" : "Show"} {restStrats.length} more strateg{restStrats.length === 1 ? "y" : "ies"}
              </button>
              {showMoreGo && (
                <div className="divide-y" style={{ borderColor: "var(--hoip-divider)" }}>
                  {restStrats.map((s) => <StrategyRow key={s.strategy_id} s={s} isSelected={selectedStrategy === s.strategy_id}
                    onSelect={() => (onUserSelectStrategy || onSelectStrategy)?.(s.strategy_id)} rankView={rankView} />)}
                </div>
              )}
            </>
          )}
          {hiddenUndefCount > 0 && (
            <div className="px-3 py-1.5 border-t text-[9px] flex items-center gap-1.5"
              style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
              data-testid="undef-risk-hidden-note">
              {hiddenUndefCount} undefined-risk strateg{hiddenUndefCount === 1 ? "y" : "ies"} hidden — enable "Show undefined-risk strategies" above to include them.
            </div>
          )}
        </div>
      )}

      {isNewEntry && goStrats.length === 0 && allStrats.length === 0 && (
        <div className="px-3 py-3 text-[10px] text-center" style={{ color: "var(--hoip-text-tertiary)" }}>
          No suitable strategies for current conditions
          {ranked.earnings?.next_date ? ` — check back after earnings on ${ranked.earnings.next_date}` : " — check back when conditions change"}.
        </div>
      )}
    </div>
  );
}

function CandlestickChart({ data, regimeBands, tapeMode = "hmm", emptyMessage }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  // Cursor position in container-pixel coords for tooltip placement. The tooltip
  // floats with the cursor (offset + flips above/below the midline) so it never
  // sits on top of the regime tape, the price ladder, or the date ruler.
  const [cursor, setCursor] = useState({ x: 0, y: 0, w: 1, h: 1 });
  if (!data || data.length === 0) {
    return (
      <div className="h-[280px] md:h-[360px] flex items-center justify-center text-xs px-4 text-center" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="chart-preparing">
        {emptyMessage || "Queued for initial history; timing will update when the next worker starts."}
      </div>
    );
  }

  // Top padding raised to fit the 5d/10d/20d majority-vote tape above the candles.
  // STRIP_H × 3 + STRIP_GAP × 2 + 5px ribbon + 4px breathing room.
  const STRIP_H = 6;
  const STRIP_GAP = 2;
  const W = 900, H = 370, PAD = { top: 28, right: 55, bottom: 30, left: 55 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const allPrices = data.flatMap(d => [d.high, d.low]).concat(
    data.map(d => d.sma50).filter(Boolean),
    data.map(d => d.sma200).filter(Boolean)
  );
  const minP = Math.floor(Math.min(...allPrices) * 0.995);
  const maxP = Math.ceil(Math.max(...allPrices) * 1.005);
  const range = maxP - minP || 1;

  const toY = (price) => PAD.top + plotH - ((price - minP) / range) * plotH;
  const candleW = Math.max(2, Math.min(8, (plotW / data.length) * 0.7));
  const gap = plotW / data.length;

  // Y-axis ticks
  const tickCount = 8;
  const yTicks = Array.from({ length: tickCount }, (_, i) => minP + (range / (tickCount - 1)) * i);

  // X-axis labels (every ~15 bars)
  const xLabelInterval = Math.max(1, Math.floor(data.length / 8));

  // Regime band lookup — color per state. `stateIdColors` keys on S0..S3 (raw
  // daily band); `labelColors` keys on the full label and powers the new
  // rolling 5d/10d/20d strips, which come from history as state_5d/10d/20d.
  const stateIdColors = { S0: "#0ea5e9", S1: "#10b981", S2: "#ef4444", S3: "#f59e0b" };
  const labelColors = {
    "Quiet Range": "#0ea5e9",
    "Bullish Trend": "#10b981",
    "Bearish Trend": "#ef4444",
    "Volatile": "#f59e0b",
  };
  const regimeMap = {};
  (regimeBands || []).forEach(r => { regimeMap[r.date] = r; });
  // Tape strip: 5-day majority + Instantaneous (daily ribbon below)
  const tape = [
    { key: "state_5d", label: "5d", y: 8 },
  ];
  const ribbonY = tape[tape.length - 1].y + STRIP_H + 4;  // raw daily ribbon sits below the tape
  const ribbonH = 5;

  // Technicals tape — single strip, rendered in the same vertical region
  // as the HMM tape so the chart's plot area never shifts when toggling.
  const techStripY = 8;
  const techStripH = STRIP_H + 4;
  const trendColors = {
    bullish:  "#10b981",
    sideways: "#71717A",
    bearish:  "#ef4444",
  };
  const showHmmTape = tapeMode === "hmm";
  const showTechTape = tapeMode === "technicals";

  const hovered = hoveredIdx !== null ? data[hoveredIdx] : null;

  return (
    <div className="relative" data-testid="chart-container">
      {hovered && (() => {
        // Smart tooltip placement — follows the cursor with a 14px offset and
        // flips above/below the chart midline so it never overlaps the regime
        // tape, the Y-axis price ladder, or the X-axis date ruler.
        // Estimate ~22px tall; container width and height come from the most
        // recent onMouseMove. The translate keeps it on-screen near edges.
        const w = cursor.w || 1;
        const h = cursor.h || 1;
        const placeBelow = cursor.y < h * 0.5;
        // Horizontal alignment: anchor by the cursor, then translate so the
        // tooltip clamps to the chart. Pure CSS, no measurement of tooltip width.
        const xPct = (cursor.x / w) * 100;
        const tx = xPct < 18 ? "0%" : xPct > 82 ? "-100%" : "-50%";
        const top = placeBelow ? `${cursor.y + 14}px` : `${cursor.y - 30}px`;
        return (
          <div
            data-testid="chart-tooltip"
            className="absolute z-10 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono-data px-2 py-1 rounded-sm whitespace-nowrap"
            style={{
              left: `${cursor.x}px`,
              top,
              transform: `translateX(${tx})`,
              pointerEvents: "none",
              background: "rgba(10,10,10,0.92)",
              border: "1px solid var(--hoip-border)",
              color: "var(--hoip-text-secondary)",
              backdropFilter: "blur(4px)",
              transition: "left 60ms linear, top 60ms linear",
              maxWidth: "min(560px, 95%)",
            }}>
            <span>{hovered.fullDate}{hovered.quoteSession ? " · quote" : ""}</span>
            <span>O:<span className="text-[var(--hoip-text-primary)]">${hovered.open.toFixed(2)}</span></span>
            <span>H:<span className="text-[var(--hoip-text-primary)]">${hovered.high.toFixed(2)}</span></span>
            <span>L:<span className="text-[var(--hoip-text-primary)]">${hovered.low.toFixed(2)}</span></span>
            <span>C:<span className={hovered.isUp ? "text-emerald-500" : "text-red-500"}>${hovered.close.toFixed(2)}</span></span>
            <span>Vol:<span className="text-[var(--hoip-text-primary)]">{(hovered.volume / 1e6).toFixed(1)}M</span></span>
            {showTechTape && hovered && (
              <span className="px-1" data-testid="chart-tooltip-trend"
                style={{ background: trendColors[hovered.trend || "sideways"], color: "#000", fontWeight: 600 }}>
                {(hovered.trend || "sideways").toUpperCase()}
              </span>
            )}
            {showHmmTape && regimeMap[hovered.fullDate] && (
              <>
                <span className="px-1" style={{ background: stateIdColors[regimeMap[hovered.fullDate].state_id], color: "#000", fontWeight: 600 }}>
                  {regimeMap[hovered.fullDate].state_label}
                </span>
                {regimeMap[hovered.fullDate].label_override && (
                  <span className="px-1 flex items-center gap-0.5"
                    style={{ color: "#EAB308", border: "1px solid rgba(234,179,8,0.4)", borderRadius: "2px" }}
                    data-testid="chart-tooltip-override">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    was {regimeMap[hovered.fullDate].raw_state_label || "?"}
                  </span>
                )}
                {regimeMap[hovered.fullDate].state_5d && (
                  <span style={{ color: "var(--hoip-text-tertiary)" }}>
                    5d:<span style={{ color: labelColors[regimeMap[hovered.fullDate].state_5d] || "#999" }} className="ml-0.5">{regimeMap[hovered.fullDate].state_5d}</span>
                  </span>
                )}
              </>
            )}
          </div>
        );
      })()}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: "auto", minHeight: 280, maxHeight: 400 }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setCursor({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            w: rect.width,
            h: rect.height,
          });
        }}
        onMouseLeave={() => setHoveredIdx(null)}>
        {/* Regime tape — 5-day majority + Instantaneous daily ribbon. */}
        {showHmmTape && tape.map(({ key, label, y }) => (
          <g key={`tape-${key}`}>
            <text x={PAD.left - 8} y={y + STRIP_H - 1} textAnchor="end"
              fontSize="7" fill="var(--hoip-text-tertiary)"
              fontFamily="'JetBrains Mono', monospace"
              data-testid={`regime-tape-label-${label}`}>
              {label}
            </text>
            {(regimeBands && regimeBands.length > 0) && data.map((d, i) => {
              const r = regimeMap[d.fullDate];
              const lbl = r && r[key];
              if (!lbl) return null;
              const x = PAD.left + i * gap;
              return (
                <rect key={`tape-${key}-${i}`} x={x} y={y} width={gap + 0.5} height={STRIP_H}
                  fill={labelColors[lbl] || "#666"} opacity={0.9} pointerEvents="none"
                  data-testid={`regime-tape-${label}-${i}`} />
              );
            })}
          </g>
        ))}
        {/* P1.4 — full-height translucent regime tints behind the candles */}
        {showHmmTape && (regimeBands && regimeBands.length > 0) && data.map((d, i) => {
          const r = regimeMap[d.fullDate];
          if (!r) return null;
          const x = PAD.left + i * gap;
          return (
            <rect key={`rbg-${i}`} x={x} y={PAD.top} width={gap + 0.5} height={plotH}
              fill={stateIdColors[r.state_id] || "#666"} opacity={0.10} pointerEvents="none"
              data-testid={`regime-bg-${i}`} />
          );
        })}
        {/* P1.4 — solid raw-daily ribbon, easy to read at a glance */}
        {showHmmTape && (regimeBands && regimeBands.length > 0) && data.map((d, i) => {
          const r = regimeMap[d.fullDate];
          if (!r) return null;
          const x = PAD.left + i * gap;
          return (
            <rect key={`rb-${i}`} x={x} y={ribbonY} width={gap + 0.5} height={ribbonH}
              fill={stateIdColors[r.state_id] || "#666"} opacity={0.9} pointerEvents="none"
              data-testid={`regime-band-${i}`} />
          );
        })}
        {/* Iter 60 — ⚠ marker on bars where the HMM label was overridden by
            the tech-vs-regime sanity layer. Lets users eyeball the historical
            edge cases (e.g. NBIS in a Quiet→Volatile correction) and validate
            the override thresholds against actual price action. */}
        {showHmmTape && (regimeBands && regimeBands.length > 0) && data.map((d, i) => {
          const r = regimeMap[d.fullDate];
          if (!r || !r.label_override) return null;
          const cx = PAD.left + i * gap + gap / 2;
          const cy = ribbonY - 3;  // sit just above the raw-daily ribbon
          // Small downward-pointing triangle in amber; sized to be visible at the
          // 120D zoom level but not overwhelming when overrides cluster.
          return (
            <g key={`override-${i}`} data-testid={`regime-override-marker-${i}`} pointerEvents="none">
              <polygon
                points={`${cx},${cy} ${cx - 2.4},${cy - 4} ${cx + 2.4},${cy - 4}`}
                fill="#EAB308"
                stroke="#92400E"
                strokeWidth={0.4}
                opacity={0.95}
              />
            </g>
          );
        })}
        {/* Iter 61 — Technicals tape: single rule-based Bull/Sideways/Bear strip
            with a clearly documented rule set. Rendered in the same vertical
            footprint as the HMM tape so toggling doesn't shift the plot. */}
        {showTechTape && (
          <g data-testid="tech-tape">
            <text x={PAD.left - 8} y={techStripY + techStripH / 2 + 2} textAnchor="end"
              fontSize="7" fill="var(--hoip-text-tertiary)"
              fontFamily="'JetBrains Mono', monospace"
              data-testid="tech-tape-label">
              TREND
            </text>
            {data.map((d, i) => {
              const trend = d.trend || "sideways";
              const x = PAD.left + i * gap;
              return (
                <rect key={`tech-${i}`} x={x} y={techStripY}
                  width={gap + 0.5} height={techStripH}
                  fill={trendColors[trend]} opacity={0.9} pointerEvents="none"
                  data-testid={`tech-tape-${i}`} />
              );
            })}
          </g>
        )}
        {/* Grid */}
        {yTicks.map((tick, i) => (
          <g key={`y-${i}`}>
            <line x1={PAD.left} y1={toY(tick)} x2={W - PAD.right} y2={toY(tick)} stroke="var(--hoip-divider)" strokeWidth={0.5} />
            <text x={PAD.left - 8} y={toY(tick) + 3} textAnchor="end" fill="var(--hoip-text-tertiary)" fontSize={9} fontFamily="JetBrains Mono">{"$"}{tick.toFixed(0)}</text>
          </g>
        ))}
        {/* X axis labels */}
        {data.map((d, i) => i % xLabelInterval === 0 ? (
          <text key={`x-${i}`} x={PAD.left + i * gap + gap / 2} y={H - 8} textAnchor="middle" fill="var(--hoip-text-tertiary)" fontSize={9} fontFamily="JetBrains Mono">{d.date}</text>
        ) : null)}

        {/* SMA 200 line */}
        <polyline fill="none" stroke="#F59E0B" strokeWidth={1.2} opacity={0.8}
          points={data.map((d, i) => d.sma200 ? `${PAD.left + i * gap + gap / 2},${toY(d.sma200)}` : "").filter(Boolean).join(" ")} />
        {/* Iter179 — Label the SMA200 gap so users understand the partial
            render at the head of the series is expected (needs 200 prior
            bars; the 120D view will always miss the leftmost ~50-80 bars
            unless the OHLCV history goes back ≥320 trading days). */}
        {data.length > 0 && !data[0].sma200 && (() => {
          const firstIdxWithSma = data.findIndex(d => d.sma200 != null);
          if (firstIdxWithSma <= 0) return null;
          const gapMidIdx = Math.floor(firstIdxWithSma / 2);
          const gx = PAD.left + gapMidIdx * gap;
          const gy = PAD.top + 14;
          return (
            <text x={gx} y={gy} textAnchor="middle" fontSize="8"
              fill="var(--hoip-text-tertiary)" fontFamily="'JetBrains Mono', monospace"
              opacity={0.65} data-testid="sma200-gap-label">
              SMA200 unavailable — needs 200 prior bars
            </text>
          );
        })()}
        {/* SMA 50 line */}
        <polyline fill="none" stroke="#3B82F6" strokeWidth={1.2} opacity={0.8}
          points={data.map((d, i) => d.sma50 ? `${PAD.left + i * gap + gap / 2},${toY(d.sma50)}` : "").filter(Boolean).join(" ")} />

        {/* Candlesticks */}
        {data.map((d, i) => {
          const x = PAD.left + i * gap + gap / 2;
          const bodyTop = toY(Math.max(d.open, d.close));
          const bodyBot = toY(Math.min(d.open, d.close));
          const bodyH = Math.max(1, bodyBot - bodyTop);
          const color = d.isUp ? "#10B981" : "#EF4444";
          return (
            <g key={i} onMouseEnter={() => setHoveredIdx(i)} style={{ cursor: "crosshair" }}>
              {/* Invisible hover area */}
              <rect x={x - gap / 2} y={PAD.top} width={gap} height={plotH} fill="transparent" />
              {/* Wick */}
              <line x1={x} y1={toY(d.high)} x2={x} y2={toY(d.low)} stroke={color} strokeWidth={1} />
              {/* Body */}
              <rect x={x - candleW / 2} y={bodyTop} width={candleW} height={bodyH}
                fill={d.isUp ? color : color} stroke={color} strokeWidth={0.5} rx={0.5} />
              {/* Hover highlight */}
              {hoveredIdx === i && (
                <line x1={x} y1={PAD.top} x2={x} y2={PAD.top + plotH} stroke="var(--hoip-text-tertiary)" strokeWidth={0.5} strokeDasharray="3 3" />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ═══ Backtest Validation Section ═══
function BacktestValidation({ bt, regimeData, techScore, regimeScore, combinedScore }) {
  const hasData = bt && bt.sample_size > 0;
  const isInsufficient = !hasData || bt?.data_quality === "insufficient" || (bt?.sample_size || 0) < 5;
  const params = hasData ? bt.params_used : null;
  const wr = hasData ? (bt.win_rate || 0) : 0;
  const ror = hasData ? ((bt.avg_return_on_risk || 0) * 100) : 0;
  const pf = hasData ? (bt.profit_factor || 0) : 0;
  const btScore = hasData && bt.backtest_score != null ? Math.round(bt.backtest_score * 100) : null;

  const Bar = ({ value, max, color, empty }) => (
    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--hoip-hover)" }}>
      {!empty && <div className="h-full rounded-full" style={{ width: `${Math.min((value / max) * 100, 100)}%`, background: color }} />}
    </div>
  );

  return (
    <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid="backtest-validation">
      {/* Signal Scores row — always rendered */}
      <div className="px-3 py-2 border-b flex flex-wrap gap-x-4 gap-y-1 text-[10px]" style={{ borderColor: "var(--hoip-divider)" }}>
        <span className="text-[9px] font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>SIGNAL SCORES</span>
        {techScore != null && <span style={{ color: "var(--hoip-text-secondary)" }}>Tech: <span className="font-mono-data font-bold">{techScore}/100</span></span>}
        {regimeScore != null && <span style={{ color: "var(--hoip-text-secondary)" }}>Regime: <span className="font-mono-data font-bold">{regimeScore}/100</span></span>}
        {combinedScore != null && <span style={{ color: "var(--hoip-text-secondary)" }}>Combined: <span className="font-mono-data font-bold">{combinedScore}/100</span></span>}
        {btScore != null ? (
          <span style={{ color: "var(--hoip-text-secondary)" }}>BT: <span className="font-mono-data font-bold">{btScore}/100</span></span>
        ) : (
          <span style={{ color: "var(--hoip-text-tertiary)" }}>BT: <span className="font-mono-data">—</span></span>
        )}
        {wr > 0 ? (
          <span style={{ color: "var(--hoip-text-secondary)" }}>WR: <span className="font-mono-data">{wr}%</span></span>
        ) : (
          <span style={{ color: "var(--hoip-text-tertiary)" }}>WR: <span className="font-mono-data">—</span></span>
        )}
      </div>

      {/* Backtest detail — data available */}
      {hasData && !isInsufficient ? (
        <div className="px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between text-[9px]">
            <span className="font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>BACKTEST</span>
            {params && <span className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }} title={bt.bt_tooltip || ""}>{params.pct_otm}% OTM · {bt.bucket_label || `${params.dte}d bucket`} · {bt.sample_size} trades</span>}
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-14 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>Win Rate</span>
              <Bar value={wr} max={100} color={wr >= 50 ? "#10B981" : "#EF4444"} />
              <span className={`font-mono-data w-12 text-right ${wr >= 50 ? "text-emerald-500" : "text-red-400"}`}>{wr}%</span>
              {pf > 0 && pf < 900 && <span className="font-mono-data text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>PF:{pf}</span>}
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-14 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>Avg RoR</span>
              <Bar value={Math.min(ror, 100)} max={100} color="#3B82F6" />
              <span className="font-mono-data w-12 text-right">{ror.toFixed(1)}%</span>
            </div>
            {btScore != null && (
              <div className="flex items-center gap-2 text-[10px]">
                <span className="w-14 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>BT Score</span>
                <Bar value={btScore} max={100} color={btScore >= 60 ? "#10B981" : btScore >= 40 ? "#F59E0B" : "#EF4444"} />
                <span className={`font-mono-data w-12 text-right font-bold ${btScore >= 60 ? "text-emerald-500" : btScore >= 40 ? "text-yellow-500" : "text-red-400"}`}>{btScore}/100</span>
              </div>
            )}
          </div>
          {/* Model Probability vs Historical Win Rate */}
          {bt.model_probability != null && (
            <div className="flex items-center gap-2 text-[9px] mt-1.5 pt-1.5 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Model prob: <span className="font-mono-data font-medium" style={{ color: "var(--hoip-text-secondary)" }}>{bt.model_probability}%</span></span>
              <span style={{ color: "var(--hoip-text-tertiary)" }}>· Historical WR: <span className="font-mono-data font-medium" style={{ color: "var(--hoip-text-secondary)" }}>{bt.historical_win_rate}%</span></span>
              {bt.model_bt_divergence && (
                <span className="px-1 py-0.5 rounded-sm font-bold" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }}>CAUTION — model/backtest divergence</span>
              )}
            </div>
          )}
          {/* Strike Logic tooltip */}
          {bt.strike_logic && (
            <div className="text-[8px] mt-1 opacity-60 cursor-help" title={bt.strike_logic} style={{ color: "var(--hoip-text-tertiary)" }} data-testid="strike-logic-tooltip">
              Strikes: backtest template + regime adj. + key levels
            </div>
          )}
          {/* Iter176 — remediation §B.17 requires an explicit
              "hypothetical results, not a guarantee" disclaimer on
              backtest tiles. Standardized wording matches the legal
              risk-disclosure page. */}
          <div className="text-[8px] italic px-3 pb-1.5" data-testid="backtest-hypothetical-disclaimer"
            style={{ color: "var(--hoip-text-tertiary)" }}>
            Hypothetical backtest — past performance is not a guarantee of future results.
          </div>
        </div>
      ) : (
        /* Backtest fallback — insufficient or no data */
        <div className="px-3 py-2 space-y-1.5" data-testid="backtest-skeleton">
          <div className="flex items-center justify-between text-[9px]">
            <span className="font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>BACKTEST</span>
            <span className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>
              {hasData ? `${bt.sample_size} trades — Insufficient data` : "Insufficient data · < 5 trades"}
            </span>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-14 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>Win Rate</span>
              <Bar value={0} max={100} color="#71717A" empty />
              <span className="font-mono-data w-12 text-right" style={{ color: "var(--hoip-text-tertiary)" }}>— (no data)</span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-14 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>Avg RoR</span>
              <Bar value={0} max={100} color="#71717A" empty />
              <span className="font-mono-data w-12 text-right" style={{ color: "var(--hoip-text-tertiary)" }}>— (no data)</span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="w-14 shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>BT Score</span>
              <Bar value={0} max={100} color="#71717A" empty />
              <span className="font-mono-data w-12 text-right" style={{ color: "var(--hoip-text-tertiary)" }}>—/100</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[9px] mt-1 pt-1 border-t" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
            <span>Strategy recently added — backtest history accumulating. Regime Fit still applies.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ Dual Panel — Fundamentals | Technical | Smart Mode ═══
function DualSignalPanel({ sig, fund, earn, regimeData, regimeLoading, strategy, keyLevels }) {
  const techRaw = sig.technical_score ?? 5;
  const fundRaw = fund.fundamental_score ?? 5;
  const techScore = Math.round(((techRaw + fundRaw) / 2) * 10);
  const regimeScore = regimeData?.score ?? null;
  const regimeVerdict = regimeData?.verdict;

  let combinedScore = null;
  let combinedVerdict = null;
  if (regimeScore !== null) {
    if (regimeScore < 30) { combinedScore = regimeScore; combinedVerdict = "NO_GO"; }
    else if (regimeScore < 55) { combinedScore = Math.round(techScore * 0.4 + regimeScore * 0.6); combinedVerdict = "CAUTION"; }
    else { combinedScore = Math.round(techScore * 0.5 + regimeScore * 0.5); combinedVerdict = "GO"; }
  }

  const divergence = regimeScore !== null ? Math.abs(techScore - regimeScore) : 0;
  const hasConflict = regimeScore !== null && divergence > 20;
  const techLabel = techScore >= 60 ? "GO" : techScore >= 40 ? "CAUTION" : "WEAK";
  const regimeState = regimeData?.regime?.state || "—";

  let conflictText = "";
  if (hasConflict && regimeScore !== null) {
    const techDir = techLabel;
    const regDir = regimeVerdict || "—";
    const reason = regimeState === "Volatile" ? "Volatile state suppressing short-premium strategies"
      : regimeState === "Bearish Trend" ? "Bearish regime — directional risk elevated"
      : regimeState === "Quiet Range" && techScore > 60 ? "Quiet regime limits directional upside"
      : "Regime confidence diverges from technical signals";
    conflictText = `Technicals say ${techDir} (score ${techScore}) but Regime says ${regDir} (score ${regimeScore}) — ${reason}`;
  }

  const vc = { GO: "#10B981", CAUTION: "#F59E0B", "NO-GO": "#EF4444", NO_GO: "#EF4444", WEAK: "#EF4444" };
  // Iter176 — remediation §B.16: display "Passes Thresholds / Marginal /
  // Fails Thresholds" instead of "GO / CAUTION / NO-GO" so the copy reads
  // as filter status, not a trade recommendation. Backend contract keeps
  // its GO/CAUTION/NO_GO codes so downstream analytics don't shift.
  const verdictDisplayLabel = {
    GO: "Passes Thresholds",
    CAUTION: "Marginal",
    NO_GO: "Fails Thresholds",
    "NO-GO": "Fails Thresholds",
    WEAK: "Below Threshold",
  };
  const resistances = keyLevels?.resistance || [];
  const supports = keyLevels?.support || [];

  return (
    <div className="space-y-0" data-testid="dual-signal-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        {/* COL 1 — Fundamentals */}
        <div className="border p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="fund-panel">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Fundamentals</h2>
            <span className="font-mono-data text-[10px] px-1 py-0.5 rounded-sm" style={{ color: "var(--hoip-text-tertiary)" }}>{fund.fundamental_score != null ? `${fund.fundamental_score}/10` : ""}</span>
          </div>
          <div className="space-y-1">
            {[
              { label: "Sector", value: fund.sector || "—" },
              { label: "P/E Ratio", value: fund.pe_ratio != null ? fund.pe_ratio.toFixed(1) : "—" },
              { label: "Rev Growth", value: fund.revenue_growth != null ? `${fund.revenue_growth > 0 ? "+" : ""}${fund.revenue_growth.toFixed(1)}%` : "—", color: fund.revenue_growth == null ? "" : (fund.revenue_growth > 0 ? "text-emerald-500" : "text-red-500") },
              { label: "Earn Growth", value: fund.earnings_growth != null ? `${fund.earnings_growth > 0 ? "+" : ""}${fund.earnings_growth.toFixed(1)}%` : "—", color: fund.earnings_growth == null ? "" : (fund.earnings_growth > 0 ? "text-emerald-500" : "text-red-500") },
              { label: "Debt/Equity", value: fund.debt_equity != null ? fund.debt_equity.toFixed(1) : "—" },
              { label: "Fund Score", value: fund.fundamental_score != null ? `${fund.fundamental_score}/10` : "—" },
              { label: "Analyst", value: fund.recommendation || "—" },
              { label: "Analysts", value: fund.analyst_count != null ? String(fund.analyst_count) : "—" },
              { label: "Next Earnings", value: earn.earnings_passed ? `${earn.next_earnings_date} (passed)` : (earn.next_earnings_date ? `${earn.next_earnings_date}${earn.next_earnings_status === "estimated" ? " (est.)" : earn.next_earnings_status === "confirmed" ? " (confirmed)" : ""}` : "Not available") },
              { label: "Days to Earn", value: earn.earnings_passed ? "Passed" : earn.days_to_earnings === 0 ? "Today" : earn.days_to_earnings != null ? `${earn.days_to_earnings}d` : "—", color: !earn.earnings_passed && earn.days_to_earnings != null && earn.days_to_earnings <= 14 && earn.days_to_earnings >= 0 ? "text-yellow-500" : "" },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex justify-between items-center text-[11px] border-b pb-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
                <span style={{ color: "var(--hoip-text-secondary)" }}>{label}</span>
                <span className={`font-mono-data font-medium ${color || ""}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* COL 2 — Technical Signal */}
        <div className="border border-l-0 md:border-l p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="tech-panel">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Technical Signal</h2>
            <span className="font-mono-data text-xs px-1.5 py-0.5 rounded-sm font-bold" style={{ color: vc[techLabel], background: `${vc[techLabel]}15` }}>{techScore}</span>
          </div>
          <div className="space-y-1">
            {[
              { label: "RSI (14)", value: sig.rsi_14 != null ? sig.rsi_14.toFixed(1) : "—", signal: sig.rsi_signal, color: sig.rsi_14 > 70 ? "text-red-500" : sig.rsi_14 < 30 ? "text-emerald-500" : "" },
              { label: "MACD", value: sig.macd_histogram != null ? sig.macd_histogram.toFixed(4) : "—", signal: sig.macd_cross, color: sig.macd_cross === "bullish" ? "text-emerald-500" : "text-red-500" },
              { label: "ADX (14)", value: sig.adx_14 != null ? sig.adx_14.toFixed(1) : "—", signal: sig.adx_strength },
              { label: "SMA 50", value: sig.sma_50 != null ? `$${sig.sma_50.toFixed(2)}` : "—" },
              { label: "SMA 200", value: sig.sma_200 != null ? `$${sig.sma_200.toFixed(2)}` : "—" },
              { label: "IV Rank", value: sig.iv_rank != null ? `${Number(sig.iv_rank).toFixed(0)}%` : (sig.iv_rank_quality === "preparing" ? "IV Rank preparing" : "—") },
              { label: "Vol Ratio", value: sig.volume_ratio != null ? `${sig.volume_ratio.toFixed(2)}x` : "—" },
              { label: "BB Pos", value: sig.price_vs_bb || "—" },
            ].map(({ label, value, signal, color }) => (
              <div key={label} className="flex justify-between items-center text-[11px] border-b pb-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
                <span style={{ color: "var(--hoip-text-secondary)" }}>{label}</span>
                <span className={`font-mono-data font-medium ${color || ""}`}>
                  {value}{signal && <span className="ml-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>({signal})</span>}
                </span>
              </div>
            ))}
          </div>
          {/* Key Levels inside Technical column */}
          {(resistances.length > 0 || supports.length > 0) && (
            <div className="border-t pt-1.5 mt-1.5 space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
              <span className="text-[9px] font-bold block" style={{ color: "var(--hoip-text-tertiary)" }}>Key Levels</span>
              {resistances.length > 0 && (
                <div className="text-[10px] flex flex-wrap gap-x-2">
                  <span style={{ color: "var(--hoip-text-tertiary)" }}>R:</span>
                  {resistances.map((r, i) => <span key={i} className="font-mono-data text-red-400">${r.level} <span className="text-[8px]">({r.label})</span></span>)}
                </div>
              )}
              {supports.length > 0 && (
                <div className="text-[10px] flex flex-wrap gap-x-2">
                  <span style={{ color: "var(--hoip-text-tertiary)" }}>S:</span>
                  {supports.map((s, i) => <span key={i} className="font-mono-data text-emerald-400">${s.level} <span className="text-[8px]">({s.label})</span></span>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* COL 3 — Smart Mode (HMM) */}
        <div className="border border-l-0 md:border-l p-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="regime-panel">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Smart Mode (HMM)</h2>
            {regimeScore !== null && <span className="font-mono-data text-xs px-1.5 py-0.5 rounded-sm font-bold" style={{ color: vc[regimeVerdict] || "#71717A", background: `${vc[regimeVerdict] || "#71717A"}15` }}>{regimeScore}</span>}
          </div>
          {regimeLoading ? (
            <div className="flex items-center gap-2 text-[10px] py-4" style={{ color: "var(--hoip-text-tertiary)" }}><RefreshCw className="w-3 h-3 animate-spin" /> Loading regime...</div>
          ) : regimeData ? (
            <div className="space-y-1">
              {[
                { label: "Regime", value: regimeData.regime?.state, extra: regimeData.regime?.prob != null ? `${(regimeData.regime.prob * 100).toFixed(0)}% confidence` : null, styleColor: stateColors[regimeData.regime?.state] },
                { label: "Verdict", value: regimeData.verdict, styleColor: vc[regimeData.verdict] },
                { label: "Sojourn", value: regimeData.regime?.sojourn_days != null ? `${regimeData.regime.sojourn_days.toFixed(0)}d` : "—" },
                { label: "P75 Sojourn", value: regimeData.regime?.sojourn_p75 != null ? `${regimeData.regime.sojourn_p75}d` : "—" },
              ].map(({ label, value, extra, styleColor }) => (
                <div key={label} className="flex justify-between items-center text-[11px] border-b pb-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
                  <span style={{ color: "var(--hoip-text-secondary)" }}>{label}</span>
                  <span className="font-mono-data font-medium flex items-center" style={styleColor ? { color: styleColor } : {}}>
                    {value}{extra && <span className="ml-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>({extra})</span>}
                    {label === "Regime" && <RegimeHelp state={value} />}
                  </span>
                </div>
              ))}
              {/* Confirmed regime — majority over last 5 bars. If different
                  from the raw daily, this is the label Position Intelligence
                  uses for strategy gating. */}
              {regimeData.confirmed_state && (
                <div data-testid="smartmode-confirmed-row"
                  className="flex justify-between items-center text-[11px] border-b pb-0.5"
                  style={{ borderColor: "var(--hoip-divider)" }}>
                  <span style={{ color: "var(--hoip-text-secondary)" }}>Confirmed (5d)</span>
                  <span className="font-mono-data font-medium"
                    style={{ color: stateColors[regimeData.confirmed_state] || "var(--hoip-text-tertiary)" }}>
                    {regimeData.confirmed_state}
                    {regimeData.confirmed_state !== regimeData.regime?.state && (
                      <span className="ml-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>(raw flip)</span>
                    )}
                  </span>
                </div>
              )}
              {REGIME_SEMANTICS[regimeData.regime?.state] && (
                <div data-testid="regime-semantics-caption"
                  className="text-[9.5px] italic leading-snug pt-1"
                  style={{ color: "var(--hoip-text-tertiary)" }}>
                  {REGIME_SEMANTICS[regimeData.regime.state]}
                </div>
              )}
              {regimeData.checks?.length > 0 && (
                <div className="pt-1 space-y-0.5">
                  <span className="text-[9px] font-bold block" style={{ color: "var(--hoip-text-tertiary)" }}>Suitability</span>
                  {regimeData.checks.map((ck, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[10px]">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: { PASS: "#10B981", CAUTION: "#F59E0B", FAIL: "#EF4444" }[ck.result] }} />
                      <span className="truncate" style={{ color: "var(--hoip-text-secondary)" }}>{ck.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[10px] py-4 text-center" style={{ color: "var(--hoip-text-tertiary)" }}>No regime data</div>
          )}
        </div>
      </div>

      {hasConflict && (
        <div data-testid="conflict-banner" className="flex items-start gap-2 px-3 py-2 border border-t-0 text-[11px]"
          style={{ background: "rgba(245,158,11,0.06)", borderColor: "rgba(245,158,11,0.25)", color: "#f59e0b" }}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /><span>{conflictText}</span>
        </div>
      )}

      {combinedScore !== null && (
        <div data-testid="combined-score" className="flex items-center justify-between px-3 py-2 border border-t-0"
          style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <span className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-secondary)" }}>Combined Score</span>
          <div className="flex items-center gap-3">
            <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
              Tech {techScore} + Regime {regimeScore}{regimeScore < 30 && " (regime hard-gate)"}
            </span>
            <span className="font-mono-data text-sm px-2 py-0.5 rounded-sm font-bold" style={{
              color: combinedVerdict === "GO" ? "#10B981" : combinedVerdict === "CAUTION" ? "#F59E0B" : "#EF4444",
              background: combinedVerdict === "GO" ? "rgba(16,185,129,0.1)" : combinedVerdict === "CAUTION" ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)"
            }}>{combinedScore}</span>
          </div>
        </div>
      )}
    </div>
  );
}
function CoveredCallWings({ ws, selectedDelta, setSelectedDelta }) {
  if (!ws?.scenarios) return null;
  const scenario = ws.scenarios[selectedDelta];
  return (
    <div className="space-y-2">
      <div>
        <span className="text-[10px] block mb-1" style={{ color: "var(--hoip-text-tertiary)" }}>Call Strike Delta</span>
        <div className="flex gap-1" data-testid="call-wing-selector">
          {[
            { key: "delta20", label: "\u039420" },
            { key: "delta30", label: "\u039430" },
            { key: "delta40", label: "\u039440" },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setSelectedDelta(key)} data-testid={`call-wing-${key}`}
              className={`px-2.5 py-1 text-xs font-mono-data border rounded-sm transition-colors ${
                selectedDelta === key
                  ? "bg-[var(--hoip-text-primary)] text-[var(--hoip-bg)] border-[var(--hoip-text-primary)]"
                  : "border-[var(--hoip-border)] text-[var(--hoip-text-secondary)] hover:text-[var(--hoip-text-primary)]"
              }`}>{label}</button>
          ))}
        </div>
      </div>
      {scenario && (
        <>
          <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)" }}>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Short Call <span className="text-[10px]">(sell)</span></span>
              <span className="font-mono-data font-medium">
                ${scenario.short_strike?.toFixed(2)} <span className="text-blue-400 text-[10px] ml-1">{"\u0394"}{scenario.short_delta}</span>
              </span>
            </div>
          </div>
          <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Premium</span>
              <span className="font-mono-data">${scenario.est_premium} / contract</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Max Profit</span>
              <span className="font-mono-data text-emerald-500">${scenario.max_profit}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Breakeven</span>
              <span className="font-mono-data">${scenario.breakeven}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══ Bear Call Spread Wing Selector ═══
function BearCallWings({ ws, selectedShort, setSelectedShort, selectedLong, setSelectedLong }) {
  if (!ws?.scenarios) return null;
  const shortGroup = ws.scenarios[selectedShort];
  const scenario = shortGroup ? shortGroup[selectedLong] : null;
  return (
    <div className="space-y-2">
      <div>
        <span className="text-[10px] block mb-1" style={{ color: "var(--hoip-text-tertiary)" }}>Short Call Delta</span>
        <div className="flex gap-1" data-testid="bcs-short-selector">
          {["delta20", "delta30", "delta40"].map(k => (
            <button key={k} onClick={() => setSelectedShort(k)} data-testid={`bcs-short-${k}`}
              className={`px-2.5 py-1 text-xs font-mono-data border rounded-sm transition-colors ${
                selectedShort === k
                  ? "bg-[var(--hoip-text-primary)] text-[var(--hoip-bg)] border-[var(--hoip-text-primary)]"
                  : "border-[var(--hoip-border)] text-[var(--hoip-text-secondary)]"
              }`}>{"\u0394"}{k.replace("delta","")}</button>
          ))}
        </div>
      </div>
      <div>
        <span className="text-[10px] block mb-1" style={{ color: "var(--hoip-text-tertiary)" }}>Long Call Delta</span>
        <div className="flex gap-1" data-testid="bcs-long-selector">
          {["delta5", "delta10", "delta15"].map(k => (
            <button key={k} onClick={() => setSelectedLong(k)} data-testid={`bcs-long-${k}`}
              className={`px-2.5 py-1 text-xs font-mono-data border rounded-sm transition-colors ${
                selectedLong === k
                  ? "bg-[var(--hoip-text-primary)] text-[var(--hoip-bg)] border-[var(--hoip-text-primary)]"
                  : "border-[var(--hoip-border)] text-[var(--hoip-text-secondary)]"
              }`}>{"\u0394"}{k.replace("delta","")}</button>
          ))}
        </div>
      </div>
      {scenario && (
        <>
          <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)" }}>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Short Call <span className="text-[10px]">(sell)</span></span>
              <span className="font-mono-data font-medium">
                ${scenario.short_strike?.toFixed(2)} <span className="text-blue-400 text-[10px] ml-1">{"\u0394"}{scenario.short_delta}</span>
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Long Call <span className="text-[10px]">(buy)</span></span>
              <span className="font-mono-data font-medium">
                ${scenario.long_strike?.toFixed(2)} <span className="text-blue-400 text-[10px] ml-1">{"\u0394"}{scenario.long_delta}</span>
              </span>
            </div>
            <div className="flex justify-between text-xs border-t pt-1 mt-1" style={{ borderColor: "var(--hoip-divider)" }}>
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Spread Width</span>
              <span className="font-mono-data font-medium text-blue-400">${scenario.spread_width?.toFixed(2)}</span>
            </div>
          </div>
          <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Max Profit</span>
              <span className="font-mono-data text-emerald-500">${scenario.max_profit}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Max Loss</span>
              <span className="font-mono-data text-red-500">${scenario.max_loss}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Return/Risk</span>
              <span className={`font-mono-data font-medium ${scenario.return_risk >= 20 ? "text-emerald-500" : "text-yellow-500"}`}>{scenario.return_risk}%</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══ Straddle Display ═══
function StraddleDisplay({ ws }) {
  if (!ws || ws.strategy !== "long_straddle") return null;
  return (
    <div className="space-y-2">
      <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)" }}>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Long Call <span className="text-[10px]">(buy)</span></span>
          <span className="font-mono-data font-medium">${ws.strike?.toFixed(2)} <span className="text-blue-400 text-[10px] ml-1">{"\u0394"}50</span></span>
        </div>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Long Put <span className="text-[10px]">(buy)</span></span>
          <span className="font-mono-data font-medium">${ws.strike?.toFixed(2)} <span className="text-blue-400 text-[10px] ml-1">{"\u0394"}50</span></span>
        </div>
      </div>
      <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Net Debit</span>
          <span className="font-mono-data">${ws.net_debit} / contract</span>
        </div>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Max Loss</span>
          <span className="font-mono-data text-red-500">${ws.max_loss}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Upside B/E</span>
          <span className="font-mono-data">${ws.upside_be}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Downside B/E</span>
          <span className="font-mono-data">${ws.downside_be}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Expected 1{"\u03C3"} Move</span>
          <span className="font-mono-data">{"\u00B1"}${ws.expected_1sigma_move}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Assessment</span>
          <span className={`font-mono-data text-[10px] ${ws.feasibility?.includes("within") ? "text-emerald-500" : "text-yellow-500"}`}>{ws.feasibility}</span>
        </div>
      </div>
      {ws.warnings?.map((w, i) => (
        <div key={i} className="flex items-center gap-1 text-yellow-500 text-[10px]"><AlertTriangle className="w-3 h-3" /> {w}</div>
      ))}
    </div>
  );
}

// ═══ Debit Call Spread Display (Part 7.2) ═══
function DebitCallSpreadDisplay({ ws }) {
  if (!ws || ws.strategy !== "debit_call_spread") return null;
  return (
    <div className="space-y-2">
      <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)" }}>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Buy Call <span className="text-[10px]">(long)</span></span>
          <span className="font-mono-data font-medium">${ws.long_strike?.toFixed(2)} <span className="text-blue-400 text-[10px] ml-1">{"\u0394"}{ws.long_delta}</span></span>
        </div>
        <div className="flex justify-between text-xs">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Sell Call <span className="text-[10px]">(short)</span></span>
          <span className="font-mono-data font-medium">${ws.short_strike?.toFixed(2)} <span className="text-blue-400 text-[10px] ml-1">{"\u0394"}{ws.short_delta}</span></span>
        </div>
        <div className="flex justify-between text-xs border-t pt-1 mt-1" style={{ borderColor: "var(--hoip-divider)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Spread Width</span>
          <span className="font-mono-data font-medium text-blue-400">${ws.spread_width?.toFixed(2)}</span>
        </div>
      </div>
      <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
        <div className="flex justify-between text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>Net Debit</span><span className="font-mono-data">${ws.net_debit} / contract</span></div>
        <div className="flex justify-between text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>Max Gain</span><span className="font-mono-data text-emerald-500">${ws.max_gain}</span></div>
        <div className="flex justify-between text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>Max Loss</span><span className="font-mono-data text-red-500">${ws.max_loss}</span></div>
        <div className="flex justify-between text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>Return/Risk</span><span className={`font-mono-data font-medium ${ws.return_risk >= 20 ? "text-emerald-500" : "text-yellow-500"}`}>{ws.return_risk}%</span></div>
        <div className="flex justify-between text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>Breakeven</span><span className="font-mono-data">${ws.breakeven}</span></div>
      </div>
    </div>
  );
}

// ═══ Universal Leg Display (replaces all individual strategy displays) ═══
function UniversalLegDisplay({ ws, strategy, strikes }) {
  if (ws && ws.valid === false) {
    return (
      <div className="border p-2 rounded-sm" data-testid="bps-invalid-structure"
        style={{ borderColor: "rgba(245,158,11,0.35)", background: "rgba(245,158,11,0.06)" }}>
        <div className="text-[11px] font-medium" style={{ color: "var(--hoip-text-secondary)" }}>
          {ws.user_message || "No validated Bull Put Spread could be constructed for this expiry from the current compact snapshot."}
        </div>
        {ws.validation_errors?.length > 0 && (
          <div className="text-[9px] mt-1 font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="bps-invalid-reason">
            {ws.validation_errors.join(" · ")}
          </div>
        )}
      </div>
    );
  }
  if (!ws || !ws.legs) {
    // No wing_scenarios — show error for unmapped strategy
    if (strikes && Object.keys(strikes).length > 0) {
      return (
        <div className="border p-2 rounded-sm" style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.05)" }}>
          <div className="text-[10px] text-yellow-500 font-medium mb-1">Leg schema missing for {strategy?.replace(/_/g, " ")} — contact support.</div>
          {Object.entries(strikes).filter(([k]) => !["spread_width", "call_spread_width"].includes(k)).map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs">
              <span className="capitalize" style={{ color: "var(--hoip-text-tertiary)" }}>{k.replace(/_/g, " ")}</span>
              <span className="font-mono-data font-medium">${typeof v === 'number' ? v.toFixed(2) : v}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  }

  const sideColor = (side) => side === "sell" ? "#EF4444" : side === "buy" ? "#3B82F6" : "#71717A";

  return (
    <div className="space-y-2" data-testid={`leg-display-${ws.strategy || strategy}`}>
      {/* Legs */}
      <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)" }}>
        {ws.legs.map((leg, i) => (
          <div key={i} className="space-y-0.5" data-testid={`leg-${i}`}>
            <div className="flex justify-between text-xs items-center">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: sideColor(leg.side) }} />
              <span style={{ color: "var(--hoip-text-tertiary)" }}>
                {leg.label} {leg.note && <span className="text-[9px] italic">({leg.note})</span>}
              </span>
              {leg.contract_id && (
                <span className="text-[8px] font-mono-data" data-testid={`leg-${i}-contract`}
                  style={{ color: "var(--hoip-text-tertiary)" }}>{leg.contract_id}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono-data font-medium">${leg.strike?.toFixed(2)}</span>
              {leg.delta_source === "unavailable" || (leg.delta == null && !leg.delta_abs) ? (
                <span className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}
                  title={leg.delta_label || "delta unavailable"}>unavailable</span>
              ) : leg.delta != null ? (
                <span className="text-blue-400 text-[10px] font-mono-data"
                      title={leg.delta_source === "modelled"
                        ? (leg.delta_label || "Modelled Δ — not provider-observed")
                        : (leg.delta_label || "Provider-observed Δ")}>
                  {leg.delta_source === "modelled" ? "~" : ""}{"\u0394"}{(
                    Math.abs(leg.delta_abs != null ? leg.delta_abs : (Math.abs(leg.delta) > 1.5 ? leg.delta / 100 : leg.delta))
                  ).toFixed(2)}{leg.delta_source === "modelled" ? " m" : ""}
                </span>
              ) : null}
            </div>
            </div>
            {(leg.bid != null || leg.ask != null || leg.last_price != null) && (
              <div className="text-[8px] font-mono-data pl-3" style={{ color: "var(--hoip-text-tertiary)" }}
                data-testid={`leg-${i}-quote`}>
                {[
                  leg.bid != null && `bid ${Number(leg.bid).toFixed(2)}`,
                  leg.ask != null && `ask ${Number(leg.ask).toFixed(2)}`,
                  leg.last_price != null && `last ${Number(leg.last_price).toFixed(2)}`,
                  leg.selected_pricing != null && `${leg.pricing_method || "mid"} ${Number(leg.selected_pricing).toFixed(2)}`,
                  leg.source_provider,
                ].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        ))}
        {(ws.expiry || ws.pricing_method || ws.as_of || ws.delta_source || ws.option_freshness) && (
          <div className="text-[8px] font-mono-data pt-1 flex items-center gap-1.5 flex-wrap" style={{ color: "var(--hoip-text-tertiary)" }}
            data-testid="leg-observation-meta"
            title={[
              ws.expiry && `Exp ${ws.expiry}`,
              ws.pricing_method,
              ws.option_freshness?.user_label || (ws.delta_source === "modelled" ? (ws.delta_label || "modelled Δ") : ws.delta_source),
              ws.option_freshness?.option_quote_as_of_et && `quote ${ws.option_freshness.option_quote_as_of_et}`,
              ws.option_freshness?.option_last_trade_at_et && `last trade ${ws.option_freshness.option_last_trade_at_et}`,
              ws.option_freshness?.fetched_at_et && `fetched ${ws.option_freshness.fetched_at_et}`,
            ].filter(Boolean).join(" · ")}>
            {ws.expiry ? <span>Exp {ws.expiry}</span> : null}
            {(() => {
              const st = primaryRowStatus({
                freshness_label: ws.option_freshness?.user_label,
                option_freshness: ws.option_freshness,
                pricing_display_label: ws.pricing_method,
                quote_complete: ws.quote_complete,
                selectable: ws.trade_selection_eligible,
              });
              return st ? <StatusBadge tier={st.tier} text={st.text} title={st.full} /> : null;
            })()}
          </div>
        )}
      </div>

      {/* Summary metrics */}
      <div className="border p-2 space-y-1" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
        {ws.legs.filter(l => l.premium != null).map((leg, i) => (
          <div key={`p${i}`} className="flex justify-between text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>{leg.label.split(" (")[0]} Prem</span>
            <span className={`font-mono-data ${leg.side === "sell" ? "text-emerald-500" : "text-red-400"}`}>
              {leg.side === "sell" ? "+" : "-"}${leg.premium?.toFixed(2)}
            </span>
          </div>
        ))}
        {/* Computed summary fields */}
        {ws.spread_width != null && <div className="flex justify-between text-[10px] border-t pt-1 mt-1" style={{ borderColor: "var(--hoip-divider)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Spread Width</span>
          <span className="font-mono-data font-medium text-blue-400">${ws.spread_width?.toFixed(2)}</span>
        </div>}
        {/* Canonical shared long-wing — method + per-spread risk geometry. */}
        {ws.long_wing && <div className="border-t pt-1 mt-1 space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }} data-testid="long-wing-panel">
          <div className="flex justify-between text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Long wing</span>
            <span className="font-mono-data text-[9px]" style={{ color: "var(--hoip-text-secondary)" }} data-testid="long-wing-method">{ws.long_wing.method_used}</span>
          </div>
          {ws.long_wing.max_loss_per_spread != null && <div className="flex justify-between text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Max loss / spread</span>
            <span className="font-mono-data text-red-400" data-testid="long-wing-maxloss">${ws.long_wing.max_loss_per_spread?.toLocaleString()}</span>
          </div>}
          {ws.long_wing.long_delta != null && <div className="flex justify-between text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Long Δ</span>
            <span className="font-mono-data text-[9px]" style={{ color: "var(--hoip-text-secondary)" }}>{ws.long_wing.long_delta?.toFixed(2)}</span>
          </div>}
          {ws.long_wing.warning && <div className="text-[10px] mt-0.5 px-1.5 py-1 rounded-sm border" data-testid="long-wing-warning"
            style={{ color: "#f59e0b", borderColor: "rgba(245,158,11,0.4)", background: "rgba(245,158,11,0.08)" }}>
            ⚠ {ws.long_wing.warning}
          </div>}
        </div>}
        {ws.call_spread_width != null && <div className="flex justify-between text-[10px] border-t pt-1 mt-1" style={{ borderColor: "var(--hoip-divider)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Call Spread Width</span>
          <span className="font-mono-data font-medium text-blue-400">${ws.call_spread_width?.toFixed(2)}</span>
        </div>}
        {ws.put_width != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Put Wing Width</span>
          <span className="font-mono-data">${ws.put_width?.toFixed(2)}</span>
        </div>}
        {ws.call_width != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Call Wing Width</span>
          <span className="font-mono-data">${ws.call_width?.toFixed(2)}</span>
        </div>}
        {ws.net_premium != null && <div className="flex justify-between text-[10px] border-t pt-1 mt-1 font-medium" style={{ borderColor: "var(--hoip-divider)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Net Premium</span>
          <span className="font-mono-data text-emerald-500">${ws.net_premium?.toFixed(2)} / contract</span>
        </div>}
        {ws.net_debit != null && <div className="flex justify-between text-[10px] border-t pt-1 mt-1 font-medium" style={{ borderColor: "var(--hoip-divider)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Net Debit</span>
          <span className="font-mono-data text-red-400">${ws.net_debit?.toFixed(2)} / contract</span>
        </div>}
        {ws.net_cost != null && <div className="flex justify-between text-[10px] border-t pt-1 mt-1 font-medium" style={{ borderColor: "var(--hoip-divider)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Net Cost</span>
          <span className={`font-mono-data ${ws.net_cost <= 0 ? "text-emerald-500" : "text-red-400"}`}>${ws.net_cost?.toFixed(2)}</span>
        </div>}
        {ws.total_debit != null && <div className="flex justify-between text-[10px] border-t pt-1 mt-1 font-medium" style={{ borderColor: "var(--hoip-divider)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Total Debit</span>
          <span className="font-mono-data text-red-400">${ws.total_debit?.toFixed(2)}</span>
        </div>}
        {ws.premium_collected != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Premium Collected</span>
          <span className="font-mono-data text-emerald-500">${ws.premium_collected}</span>
        </div>}
        {ws.cash_required != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Cash Required</span>
          <span className="font-mono-data">${ws.cash_required?.toLocaleString()}</span>
        </div>}
        {ws.hedge_cost != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Hedge Cost</span>
          <span className="font-mono-data text-red-400">${ws.hedge_cost}</span>
        </div>}
        {ws.max_profit != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Max Profit</span>
          <span className="font-mono-data text-emerald-500">${ws.max_profit}</span>
        </div>}
        {ws.max_loss != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Max Loss</span>
          <span className="font-mono-data text-red-400">${ws.max_loss}</span>
        </div>}
        {ws.breakeven != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Breakeven</span>
          <span className="font-mono-data" title={ws.input_quality_label || undefined}>${ws.breakeven?.toFixed(2)}</span>
        </div>}
        {ws.breakeven_up != null && <div className="flex justify-between text-[10px]">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>BE Up/Down</span>
          <span className="font-mono-data">${ws.breakeven_up?.toFixed(2)} / ${ws.breakeven_down?.toFixed(2)}</span>
        </div>}
        {ws.iv_guidance && <div className="flex justify-between text-[10px] border-t pt-1 mt-1" style={{ borderColor: "var(--hoip-divider)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>IV Environment</span>
          <span className={`font-mono-data text-[9px] ${ws.iv_rank < 30 ? "text-emerald-500" : ws.iv_rank > 60 ? "text-red-400" : "text-yellow-500"}`}>{ws.iv_guidance}</span>
        </div>}
        {/* Delta band + No-touch probability for Wheel strategies */}
        {ws.delta_band_used && <div className="flex justify-between text-[10px] border-t pt-1 mt-1" style={{ borderColor: "var(--hoip-divider)" }} data-testid="delta-band-used">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Delta Band</span>
          <span className="font-mono-data text-[9px]" style={{ color: "var(--hoip-text-secondary)" }}>{ws.delta_band_used}</span>
        </div>}
        {(ws.pop_pct != null || (() => {
          const sell = (ws.legs || []).find(l => l.side === "sell" && l.delta != null);
          return sell;
        })()) && <div className="flex justify-between text-[10px]" data-testid="pop-metric">
          <span style={{ color: "var(--hoip-text-tertiary)" }} title="Probability of Profit — tastytrade-style 1 − |Δ| approximation, or broker greeks when linked">POP</span>
          <span className="font-mono-data text-[9px]" style={{ color: "var(--hoip-text-secondary)" }}
            title={ws.input_quality_label ? `Probability of Profit — 1 − |Δ| · ${ws.input_quality_label}` : undefined}>
            {ws.pop_pct != null ? `${ws.pop_pct}%` : (() => {
              const sell = (ws.legs || []).find(l => l.side === "sell" && l.delta != null);
              if (!sell) return "—";
              let d = Math.abs(sell.delta);
              if (d > 1.5) d = d / 100;
              return `${Math.round((1 - d) * 1000) / 10}%`;
            })()}
          </span>
        </div>}
        {(ws.pot_pct != null || ws.p_no_touch != null) && <div className="flex justify-between items-center text-[10px]" data-testid="pot-metric">
          <span style={{ color: "var(--hoip-text-tertiary)" }} title="Probability of Touch = 1 − Probability of Non-Touch">POT</span>
          <span className="font-mono-data text-[9px]" style={{ color: "var(--hoip-text-secondary)" }}
            title={ws.input_quality_label ? `Probability of Touch · ${ws.input_quality_label}` : undefined}>
            {ws.pot_pct != null ? `${ws.pot_pct}%` : `${Math.round((100 - ws.p_no_touch) * 10) / 10}%`}
          </span>
        </div>}
        {/* Iter 60.3 — Volume-profile context tag for the short strike.
            Informational only; does not affect scoring or strike selection.
            Computed in the backend from the trailing 120D volume-by-price
            profile (top 70% value area). */}
        {ws.volume_profile && ws.volume_profile.label && (
          <div className="flex justify-between items-center text-[10px]"
            data-testid="volume-profile-tag"
            title={(() => {
              const vp = ws.volume_profile;
              const anchorLabel = {
                regime_start: `anchored to ${vp.regime_label || "regime"} start`,
                fixed_60: "trending regime — 60-day default",
                volatile_cap: "Volatile regime — 120-day cap",
                fixed: "fixed window",
              }[vp.lookback_anchor] || "fixed window";
              return `Value area: $${vp.value_area_low} – $${vp.value_area_high} · POC $${vp.poc} · ${vp.lookback_bars}D lookback (${anchorLabel})`;
            })()}>
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Volume profile</span>
            <span className="font-mono-data text-[9px] flex items-center gap-1" style={{ color: "var(--hoip-text-tertiary)" }}>
              <span>strike {ws.volume_profile.label}</span>
              <span className="opacity-60">· {ws.volume_profile.lookback_bars}D</span>
            </span>
          </div>
        )}
      </div>

      {/* Premium yield for premium-selling strategies */}
      {ws.premium_yield != null && (
        <div className="flex justify-between text-[10px] px-2 py-1 border rounded-sm" style={{ borderColor: "var(--hoip-border)" }}>
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Premium Yield</span>
          <span className="font-mono-data font-medium" style={{ color: ws.premium_yield_color || "var(--hoip-text-primary)" }}>
            {ws.premium_yield_label}
          </span>
        </div>
      )}

      {/* Premium insufficient warning */}
      {ws.premium_warning && (
        <div className="flex items-start gap-1.5 text-[10px] px-2 py-1.5 border rounded-sm"
          style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.05)", color: "#F59E0B" }}
          data-testid="premium-warning">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{ws.premium_warning}</span>
        </div>
      )}

      {/* Strike safety / premium floor warning */}
      {ws.strike_warning && (
        <div className={`flex items-start gap-1.5 text-[10px] px-2 py-1.5 border rounded-sm ${
          ws.strike_warning.level === "danger" || ws.strike_warning.type === "premium_floor"
            ? "border-red-500/30 bg-red-500/5 text-red-400"
            : "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"
        }`} data-testid="strike-safety-warning">
          {ws.strike_warning.level === "danger" || ws.strike_warning.type === "premium_floor"
            ? <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            : <CheckCircle className="w-3 h-3 mt-0.5 shrink-0" />}
          <span>{ws.strike_warning.message}</span>
        </div>
      )}

      {/* Recovery analytics (Covered Call loss positions) */}
      {ws.recovery && (
        <div className="border rounded-sm p-2 space-y-1" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }} data-testid="recovery-analytics">
          <span className="text-[9px] font-bold block" style={{ color: "var(--hoip-text-tertiary)" }}>RECOVERY ANALYTICS</span>
          <div className="flex justify-between text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Unrealised loss</span>
            <span className="font-mono-data text-red-400">-${ws.recovery.loss_per_share} / share (-{ws.recovery.loss_pct}%)</span>
          </div>
          <div className="flex justify-between text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>This premium recovers</span>
            <span className="font-mono-data text-emerald-500">${ws.recovery.premium_recovers} / share ({ws.recovery.recovery_pct}% of loss)</span>
          </div>
          <div className="flex justify-between text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Rolls to full recovery</span>
            <span className={`font-mono-data font-medium ${ws.recovery.rolls_to_recover > 12 ? "text-yellow-500" : "text-emerald-500"}`}>
              ~{ws.recovery.rolls_to_recover} monthly cycle{ws.recovery.rolls_to_recover !== 1 ? "s" : ""}
            </span>
          </div>
          {ws.recovery.strike_label && (
            <div className="text-[9px] pt-1 border-t" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
              {ws.recovery.strike_label}
            </div>
          )}
          {ws.recovery.warning && (
            <div className="text-[9px] pt-1 text-yellow-500">{ws.recovery.warning}</div>
          )}
        </div>
      )}

      {/* Jade Lizard / strategy validation */}
      {ws.validation_warning && (
        <div className="flex items-start gap-1.5 text-yellow-500 text-[10px] px-2 py-1.5 border rounded-sm"
          style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.05)" }} data-testid="jade-lizard-warning">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>{ws.validation_warning}</span>
        </div>
      )}
      {ws.is_valid_jade && (
        <div className="flex items-center gap-1.5 text-emerald-500 text-[10px] px-2 py-1.5 border rounded-sm"
          style={{ borderColor: "rgba(16,185,129,0.3)", background: "rgba(16,185,129,0.05)" }}>
          <CheckCircle className="w-3 h-3" />
          <span>Valid Jade Lizard {"\u2014"} net credit (${ws.net_premium?.toFixed(2)}) covers call spread width (${ws.call_spread_width?.toFixed(2)}). Zero upside risk.</span>
        </div>
      )}
    </div>
  );
}


// ═══ Regime Verdict Badge ═══
function RegimeVerdictBadge({ verdict, score }) {
  if (!verdict) return null;
  const colors = { GO: "#10B981", CAUTION: "#F59E0B", "NO-GO": "#EF4444" };
  const bg = { GO: "rgba(16,185,129,0.1)", CAUTION: "rgba(245,158,11,0.1)", "NO-GO": "rgba(239,68,68,0.1)" };
  // Iter176 — display "Passes / Marginal / Fails" instead of GO/CAUTION/NO-GO
  // per remediation §B.16. Backend contract unchanged.
  const displayLabel = { GO: "Passes", CAUTION: "Marginal", "NO-GO": "Fails" };
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold font-mono-data"
      style={{ color: colors[verdict], background: bg[verdict] }}
      data-testid={`regime-badge-${verdict?.toLowerCase()}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: colors[verdict] }} />
      {displayLabel[verdict] || verdict} ({score})
    </span>
  );
}

// ═══ Regime State Pill ═══
function RegimeStatePill({ state, prob }) {
  const stateColors = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };
  const color = stateColors[state] || "#71717A";
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold font-mono-data"
      style={{ color, background: `${color}15` }}>
      {state} {prob != null && <span className="font-normal">{(prob * 100).toFixed(0)}%</span>}
      <RegimeHelp state={state} />
    </span>
  );
}

// ═══ Regime Suitability Panel (shown in Regime Engine mode) ═══
function RegimeSuitabilityPanel({ data, onSuggest, suggestions, suggestLoading }) {
  if (!data) return null;
  const colors = { GO: "#10B981", CAUTION: "#F59E0B", "NO-GO": "#EF4444" };
  const v = data.verdict;
  const c = colors[v] || "#71717A";
  const showSuggest = v === "NO-GO" || v === "CAUTION";
  return (
    <div className="border rounded-sm p-2.5 space-y-2" style={{ borderColor: `${c}40`, background: `${c}08` }} data-testid="regime-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gauge className="w-3.5 h-3.5" style={{ color: c }} />
          <RegimeStatePill state={data.regime?.state} prob={data.regime?.prob} />
        </div>
        <RegimeVerdictBadge verdict={v} score={data.score} />
      </div>

      {/* Suitability Checks */}
      <div className="space-y-0.5">
        {data.checks?.map((ck, i) => {
          const ckColors = { PASS: "#10B981", CAUTION: "#F59E0B", FAIL: "#EF4444" };
          return (
            <div key={i} className="flex items-center gap-2 text-[10px]">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: ckColors[ck.result] }} />
              <span className="w-28 truncate" style={{ color: "var(--hoip-text-tertiary)" }}>{ck.name}</span>
              <span className="flex-1" style={{ color: "var(--hoip-text-secondary)" }}>{ck.detail}</span>
            </div>
          );
        })}
      </div>

      {/* Regime Stats */}
      <div className="flex flex-wrap gap-3 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span>Sojourn: <span className="font-mono-data">{data.regime?.sojourn_days?.toFixed(0)}d</span></span>
        <span>Entropy: <span className="font-mono-data">{data.regime?.entropy?.toFixed(2)}</span></span>
        <span>Danger: <span className="font-mono-data" style={{ color: data.regime?.danger_prob > 0.3 ? "#EF4444" : "inherit" }}>{(data.regime?.danger_prob * 100)?.toFixed(0)}%</span></span>
      </div>

      {/* Rationale */}
      <div className="text-[10px] px-2 py-1 border rounded-sm" style={{ borderColor: `${c}30`, color: "var(--hoip-text-secondary)" }}>
        {data.rationale}
      </div>

      {/* Strategy-Regime mapping */}
      {data.strategy_profile && (
        <div className="flex flex-wrap gap-2 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          <span>Ideal: {data.strategy_profile.ideal_regimes?.join(", ")}</span>
          {data.strategy_profile.danger_regimes?.length > 0 && <span>Danger: <span className="text-red-400">{data.strategy_profile.danger_regimes?.join(", ")}</span></span>}
        </div>
      )}

      {/* Suggest Regime-Appropriate Strategy */}
      {showSuggest && (
        <div className="pt-1 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
          {!suggestions && (
            <button onClick={onSuggest} disabled={suggestLoading} className="flex items-center gap-1.5 text-[10px] px-3 py-1.5 border rounded-sm w-full justify-center transition-colors hover:bg-[var(--hoip-hover)]"
              style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }} data-testid="suggest-regime-strategy">
              {suggestLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Gauge className="w-3 h-3" />}
              Suggest strategies suited for current {data.regime?.state} regime
            </button>
          )}
          {suggestions?.length > 0 && (
            <div className="space-y-1 mt-1">
              <span className="text-[9px] font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>Regime-suited strategies for {data.regime?.state}:</span>
              {suggestions.map((s, i) => {
                const sColors = { GO: "#10B981", CAUTION: "#F59E0B" };
                return (
                  <div key={i} className="flex items-center gap-2 text-[10px] px-2 py-1 border rounded-sm" style={{ borderColor: `${sColors[s.verdict]}30`, background: `${sColors[s.verdict]}06` }}>
                    <span className="px-1 py-0.5 rounded-sm font-bold text-[9px]" style={{ color: sColors[s.verdict], background: `${sColors[s.verdict]}15` }}>{s.verdict}</span>
                    <span className="font-medium" style={{ color: "var(--hoip-text-primary)" }}>{s.strategy_name}</span>
                    <span className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>({s.score})</span>
                    <span className="flex-1 truncate" style={{ color: "var(--hoip-text-tertiary)" }}>{s.direction?.replace(/_/g, " ")}</span>
                  </div>
                );
              })}
            </div>
          )}
          {suggestions?.length === 0 && <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>No suitable strategies for this regime.</p>}
        </div>
      )}
    </div>
  );
}

// ═══ Strategy Card with Wing Selector + Key Levels + Earnings + Backtest ═══

function StrategyCard({ rec, wingScenarios, keyLevels, selectedWing, setSelectedWing, selectedCallWing, setSelectedCallWing, selectedBcsShort, setSelectedBcsShort, selectedBcsLong, setSelectedBcsLong, backtestValidation, spot, regimeData, techScore, regimeScore, combinedScore, ivTermData, snappedExpiry, symbol, activeDte, user, chainExpiry }) {
  const ws = wingScenarios;
  const earningsState = rec.earnings_state || {};
  const confidenceState = rec.confidence_state || {};
  const warnings = rec.warnings || [];
  const techRef = rec.technical_reference;
  const strategy = rec.strategy;

  const isDCS = false; // deprecated - using UniversalLegDisplay
  const isPutSpread = false;
  const isCoveredCall = false;
  const isBearCallSpread = false;
  const isStraddle = false;
  const isJadeLizard = false;
  const activeScenario = null;

  return (
    <div className="space-y-3" data-testid="strategy-card">
      {/* Volatile-5d down-weight banner — spec §"BPS × Volatile".
          Shows when 5d smoothed regime is Volatile AND the picked strategy
          is a premium-seller. `ws.regime_downweight_flag` is emitted by the
          /ticker/{sym}/data route in the same paint. */}
      {ws?.freshness_banner && (() => {
        const bannerTier = statusSeverity(ws.freshness_banner, {
          status: ws.option_freshness?.status,
          quote_complete: ws.quote_complete,
        });
        if (bannerTier === "info") return null;
        const tone = statusToneStyle(bannerTier);
        return (
          <div
            data-testid="option-freshness-banner"
            className="flex items-start gap-2 py-1"
            style={bannerTier === "blocking" ? { borderLeft: `2px solid ${tone.color}`, paddingLeft: 8 } : undefined}>
            {bannerTier === "blocking" ? (
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: tone.color }} />
            ) : (
              <span className="mt-0.5 text-[8px]" style={{ color: tone.color }} aria-hidden="true">▲</span>
            )}
            <div className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-secondary)" }}>
              {ws.freshness_banner}
            </div>
          </div>
        );
      })()}
      {ws?.regime_downweight_flag?.active && (
        <div
          data-testid="strategy-downweight-banner"
          className="flex items-start gap-2 border p-2 rounded-sm"
          style={{
            background: "rgba(245,158,11,0.08)",
            borderColor: "rgba(245,158,11,0.35)",
          }}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "#F59E0B" }} />
          <div className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-secondary)" }}>
            <span className="font-bold" style={{ color: "#F59E0B" }}>Volatile 5d — premium collection down-weighted.</span>{" "}
            {ws.regime_downweight_flag.banner?.replace(/^Volatile 5d[^.]*\.\s*/, "") ||
             "Expect wider expected moves; consider sizing accordingly."}
          </div>
        </div>
      )}

      {/* 1. Strategy Title */}
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-emerald-500" />
        <span className="text-sm font-medium capitalize">{strategy?.replace(/_/g, " ")}</span>
        {ivTermData?.iv_crush_expiries?.length > 0 && snappedExpiry && ivTermData.iv_crush_expiries.includes(snappedExpiry) && (
          <span className="text-[8px] px-1.5 py-0.5 rounded-sm font-bold" style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B" }} data-testid="iv-crush-badge">IV Crush Risk</span>
        )}
        {/* P1.9 — Trade button opens order ticket dialog with prefilled deep-link */}
        {ws?.valid !== false && ws?.blocks_selection !== true && (
        <button
          data-testid="strategy-trade-btn"
          onClick={() => window.dispatchEvent(new CustomEvent("markovlab:open-order-ticket", {
            detail: {
              symbol,
              strategy,
              short_strike: rec.short_strike || ws?.legs?.find(l => l.side === "sell")?.strike,
              long_strike: rec.long_strike || ws?.legs?.find(l => l.side === "buy")?.strike,
              expiry: snappedExpiry,
              qty: 1,
              premium: rec.premium ?? ws?.net_premium,
              short_premium: ws?.legs?.find(l => l.side === "sell")?.premium,
              long_premium: ws?.legs?.find(l => l.side === "buy")?.premium,
              side: rec.side || "credit",
            }
          }))}
          className="ml-auto text-[10px] px-2 py-0.5 font-bold tracking-widest"
          style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }}
        >
          Open in Broker ↗
        </button>
        )}
      </div>

      {/* 2. Earnings Flag */}
      {earningsState.flag && (
        <div className={`text-xs px-2 py-1.5 border rounded-sm ${
          earningsState.level === "CRITICAL" ? "border-red-700 bg-red-950/30 text-red-400" :
          earningsState.level === "HIGH" ? "border-yellow-700 bg-yellow-950/30 text-yellow-400" :
          "border-yellow-800 bg-yellow-950/20 text-yellow-500"
        }`} data-testid="earnings-flag">
          <div className="flex items-center gap-1 font-medium"><AlertTriangle className="w-3 h-3" /> Earnings Risk: {earningsState.level}</div>
          <p className="mt-0.5 text-[10px] leading-snug opacity-90">{earningsState.message}</p>
          {earningsState.alt_dte && <p className="mt-0.5 text-[10px] opacity-70">Alt DTE: {earningsState.alt_dte}</p>}
        </div>
      )}

      {/* 3. Description */}
      {ws?.valid !== false && (
      <p className="text-xs leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>{rec.reason}</p>
      )}

      {/* 4. Universal Leg Display */}
      <UniversalLegDisplay ws={ws} strategy={strategy} strikes={rec.strikes} />

      {/* Iter191 — UOA context panel (Long-Vol strategies only; gated inside) */}
      <UOAPanel symbol={symbol} strategy={strategy} expiry={chainExpiry} />

      {/* B3 — permanent analytics disclaimer below the Readiness Check */}
      <p className="text-[9px] leading-snug italic" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="analytics-disclaimer">
        {ANALYTICS_DISCLAIMER}
      </p>

      {/* 8. DTE / Entry / Stop / Confidence */}
      {ws?.valid !== false && (
      <div className="space-y-1 border-t pt-2" style={{ borderColor: "var(--hoip-divider)" }}>
        {rec.dte_range && <div className="text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>DTE:</span> <span className="font-mono-data">{rec.dte_range}</span></div>}
        {rec.entry_trigger && <div className="text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>Entry:</span> <span className="font-mono-data">{rec.entry_trigger}</span></div>}
        {rec.stop_level && <div className="text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>Stop:</span> <span className="font-mono-data text-red-500">${rec.stop_level}</span></div>}
        {rec.stop_note && <div className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{rec.stop_note}</div>}

        {/* Blended Confidence Display (Part 4.4) */}
        {confidenceState.confidence_pct != null ? (
          <div className="space-y-1">
            <div className="text-xs">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Model Score:</span>{" "}
              <span className={`font-mono-data font-bold ${confidenceState.confidence_pct >= 70 ? "text-emerald-500" : confidenceState.confidence_pct >= 50 ? "text-yellow-500" : "text-red-400"}`}>
                {confidenceState.confidence_pct}%
              </span>
              <span className="text-[10px] ml-1" style={{ color: "var(--hoip-text-tertiary)" }}>({confidenceState.label})</span>
            </div>
            <div className="flex gap-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
              <span>Tech: <span className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{confidenceState.technical_score}%</span></span>
              {confidenceState.backtest_score != null && (
                <span>BT: <span className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{confidenceState.backtest_score}% <span className="text-[9px]">({Math.round(confidenceState.backtest_credibility * 100)}% cred)</span></span></span>
              )}
              {confidenceState.earnings_modifier < 1 && (
                <span>Earn: <span className="font-mono-data text-yellow-500">{"\u00D7"}{confidenceState.earnings_modifier.toFixed(2)}</span></span>
              )}
            </div>
            {confidenceState.data_note && <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{confidenceState.data_note}</div>}
          </div>
        ) : confidenceState.final > 0 ? (
          <div className="text-xs">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>Model Score:</span>{" "}
            <span className={`font-mono-data font-medium ${confidenceState.final >= 0.6 ? "text-emerald-500" : confidenceState.final >= 0.4 ? "text-yellow-500" : "text-red-400"}`}>
              {(confidenceState.final * 100).toFixed(0)}%
            </span>
          </div>
        ) : rec.confidence > 0 ? (
          <div className="text-xs"><span style={{ color: "var(--hoip-text-tertiary)" }}>Model Score:</span> <span className="font-mono-data font-medium">{(rec.confidence * 100).toFixed(0)}%</span></div>
        ) : null}
      </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-1 border-t pt-2" style={{ borderColor: "var(--hoip-divider)" }}>
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1 text-yellow-500 text-[10px]">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* 9. Backtest Validation (above Key Levels) — always rendered */}
      <BacktestValidation bt={backtestValidation}
        regimeData={regimeData} techScore={techScore} regimeScore={regimeScore} combinedScore={combinedScore} />

      {/* 10. Key Levels */}
      {keyLevels && <KeyLevelsSection levels={keyLevels} />}
    </div>
  );
}

function KeyLevelsSection({ levels }) {
  if (!levels || (!levels.resistance?.length && !levels.support?.length)) return null;
  return (
    <div className="border-t pt-2 mt-2 space-y-1" style={{ borderColor: "var(--hoip-divider)" }} data-testid="key-levels">
      <span className="text-[10px] font-bold block" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-secondary)" }}>Key Levels</span>
      {levels.resistance?.length > 0 && (
        <div className="text-xs flex flex-wrap gap-x-3">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Resistance:</span>
          {levels.resistance.map((r, i) => (
            <span key={i} className="font-mono-data text-red-400">${r.level} <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>({r.label})</span></span>
          ))}
        </div>
      )}
      {levels.support?.length > 0 && (
        <div className="text-xs flex flex-wrap gap-x-3">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>Support:</span>
          {levels.support.map((s, i) => (
            <span key={i} className="font-mono-data text-emerald-500">${s.level} <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>({s.label})</span></span>
          ))}
        </div>
      )}
    </div>
  );
}

// Session-persisted Strike Mode PER TICKER. sessionStorage-backed so it
// survives route unmount/remount (and React StrictMode double-mount) while
// still resetting on a full browser-tab close. AMZN in Delta mode leaves
// AAPL in P(NT) mode.
const _STRIKE_MODE_KEY = "mklab.strikeModeBySymbol";
const _readStrikeModes = () => {
  try { return JSON.parse(sessionStorage.getItem(_STRIKE_MODE_KEY)) || {}; }
  catch { return {}; }
};
const _writeStrikeMode = (symbol, mode) => {
  if (!symbol) return;
  try {
    const m = _readStrikeModes();
    m[symbol] = mode;
    sessionStorage.setItem(_STRIKE_MODE_KEY, JSON.stringify(m));
  } catch { /* ignore quota/serialization errors */ }
};

const STRIKE_MODE_OPTS = [
  { k: "delta", l: "Δ Regime-Scaled" },
  { k: "key_levels", l: "Key Levels" },
  { k: "pnt", l: "P(NT)" },
];

// ═══ Regime Backtest Context — historical per-regime performance of THIS
//     strategy on THIS ticker, pulled from the backtest engine. Replaces the
//     binary "Regime Fit / NO-GO" line. Verdict is DERIVED FROM THE NUMBERS. ═══
const _RBC_REGIMES = ["Quiet Range", "Bullish Trend", "Bearish Trend", "Volatile"]; // all 4 regimes
const _RBC_SHORT = { "Quiet Range": "Quiet", "Bullish Trend": "Bullish", "Bearish Trend": "Bearish", "Volatile": "Volatile" };

function RegimeBacktestContext({ symbol, strategy, dte, regimeData, ctx: ctxProp, loadingProp }) {
  const [ctxState, setCtx] = useState(null);
  const [loadingState, setLoading] = useState(false);
  const PREMIUM = ["bull_put_spread", "cash_secured_put", "short_put", "covered_call"];
  const supported = PREMIUM.includes(strategy);
  const usingParent = ctxProp !== undefined;
  const ctx = usingParent ? ctxProp : ctxState;
  const loading = usingParent ? loadingProp : loadingState;

  useEffect(() => {
    if (usingParent || !symbol || !supported) { if (!usingParent) setCtx(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/ticker/${symbol}/regime-backtest-context`, { params: { strategy, dte: dte || 30 } })
      .then(({ data }) => { if (!cancelled) setCtx(data); })
      .catch(() => { if (!cancelled) setCtx(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, strategy, dte, supported, usingParent]);

  if (!supported) {
    return (
      <div className="text-[10px] px-2 py-1.5 mb-2 border rounded-sm" data-testid="regime-context-na"
        style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>
        Regime backtest context is available for premium-selling strategies (CSP / BPS / Covered Call).
      </div>
    );
  }

  const cur = ctx?.current_regime || regimeData?.regime?.state;
  const conf = regimeData?.regime?.prob != null ? `${(regimeData.regime.prob * 100).toFixed(0)}% confidence` : null;
  const ageBars = regimeData?.regime?.sojourn_days != null ? `${regimeData.regime.sojourn_days.toFixed(0)}d in` : null;
  const tones = { green: "#10B981", amber: "#F59E0B", red: "#EF4444", neutral: "var(--hoip-text-tertiary)" };
  const rows = [
    { key: "win_rate", label: "Win rate", fmt: (v) => `${v}%` },
    { key: "assign_rate", label: "Assign rate", fmt: (v) => `${v}%` },
    { key: "pct_otm_short", label: "%OTM short", fmt: (v) => `${v}%` },
  ];

  return (
    <div className="mt-5 pt-4 mb-3 border-t" data-testid="regime-context-wrap" style={{ borderColor: "var(--hoip-divider)" }}>
    <div className="border rounded-sm overflow-hidden" data-testid="regime-context-panel"
      style={{ borderColor: "var(--hoip-border)" }}>
      <div className="px-2.5 py-1.5 flex items-center justify-between text-[10px] border-b"
        style={{ borderColor: "var(--hoip-divider)", background: "var(--hoip-hover)" }}>
        <span className="font-bold tracking-wide" style={{ color: "var(--hoip-text-primary)" }} title="REGIME CONTEXT — strategy × regime historical win-rate and EV on THIS ticker, computed from the price-history backtest at THIS DTE. Distinct from 'Historical Regime Edge' below, which is a broader Strike-Selection Lab aggregate across DTE/strike buckets. Numbers are expected to differ.">REGIME CONTEXT <span className="text-[9px] font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>· ticker × strategy × current DTE</span></span>
        <span data-testid="regime-context-current" style={{ color: stateColors[cur] || "var(--hoip-text-secondary)" }}>
          {cur || "—"}{conf ? ` (${conf})` : ""}{ageBars ? ` · ${ageBars}` : ""}
        </span>
      </div>

      {loading && <div className="px-2.5 py-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="regime-context-loading">Computing regime backtest context…</div>}

      {!loading && ctx?.insufficient && (
        <div className="px-2.5 py-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Not enough historical data to build regime context for this ticker.</div>
      )}

      {!loading && ctx?.regimes && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] font-mono-data" data-testid="regime-context-table">
              <thead>
                <tr style={{ color: "var(--hoip-text-tertiary)" }}>
                  <th className="text-left font-normal px-2.5 py-1"> </th>
                  {_RBC_REGIMES.map((r) => {
                    const isCur = r === cur;
                    return (
                      <th key={r} className="text-right font-bold px-2 py-1"
                        data-testid={`regime-col-${r.replace(/\s+/g, "-").toLowerCase()}`}
                        style={{ color: isCur ? stateColors[r] : "var(--hoip-text-secondary)", borderLeft: isCur ? `2px solid ${stateColors[r] || "#3B82F6"}` : "none", borderRight: isCur ? `2px solid ${stateColors[r] || "#3B82F6"}` : "none" }}>
                        {_RBC_SHORT[r]}{isCur ? " ←" : ""}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key} className="border-t" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="text-left px-2.5 py-1" style={{ color: "var(--hoip-text-tertiary)" }}>{row.label}</td>
                    {_RBC_REGIMES.map((r) => {
                      const d = ctx.regimes[r] || {};
                      const isCur = r === cur;
                      const val = d.insufficient || d[row.key] == null ? "—" : row.fmt(d[row.key]);
                      const col = (!d.insufficient && row.color) ? row.color(d[row.key]) : "var(--hoip-text-primary)";
                      return (
                        <td key={r} className="text-right px-2 py-1"
                          data-testid={`regime-cell-${r.replace(/\s+/g, "-").toLowerCase()}-${row.key}`}
                          style={{ color: col, borderLeft: isCur ? `2px solid ${stateColors[r] || "#3B82F6"}` : "none", borderRight: isCur ? `2px solid ${stateColors[r] || "#3B82F6"}` : "none", fontWeight: isCur ? 700 : 400 }}>
                          {val}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ctx.verdict && (
            <div className="px-2.5 py-1.5 text-[10px] border-t flex items-start gap-1.5" data-testid="regime-context-verdict"
              style={{ borderColor: "var(--hoip-divider)", color: tones[ctx.verdict.tone] || tones.neutral, background: `${(tones[ctx.verdict.tone] || "#71717A")}0d` }}>
              <span className="font-bold mt-px">{ctx.verdict.tone === "green" ? "✓" : ctx.verdict.tone === "red" ? "✕" : "!"}</span>
              <span>{ctx.verdict.text}</span>
            </div>
          )}
          <div className="px-2.5 py-1 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            Historical {strategy.replace(/_/g, " ")} entries bucketed by regime at entry · {dte || 30}d cycles · {ctx.total_samples || 0} samples
          </div>
        </>
      )}

      {!loading && !ctx && (
        <div className="px-2.5 py-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Regime context unavailable.</div>
      )}
    </div>
    </div>
  );
}



export default function TickerWorkbench() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // ── Phase 2: cross-module pass-through ──
  // The Screener row's resolved snapshot (regime label, expiry, DTE, short
  // delta, P(NT), φ) is forwarded via router state so the Workbench seeds its
  // initial render from the SAME analytics payload the row showed — no
  // net-new chain fetch on mount, no metric drift between the two surfaces.
  // Refresh-safe: when state is missing the workbench falls back to its own
  // fetch chain (the shared `cached_infer_regime` SSOT still guarantees the
  // regime label matches).
  const screenerSnapshot = location?.state?.screenerSnapshot || null;
  // Iter 103 — screener-rebuild telemetry forwarded via router state (no new
  // API call). Renders a small "Last rebuild" chip in the header so the user
  // sees WHEN and via WHICH provider the inherited row was built.
  const screenerEvidenceAsOf = location?.state?.screenerEvidenceAsOf || null;
  const screenerBuiltByProvider = location?.state?.screenerBuiltByProvider || null;
  // Compute the relative-age label ONCE per (route-state) render — pure derivation
  // so React's strict purity rules don't trip on a direct Date.now() in JSX. The
  // chip is informational; a refresh re-renders it with a new "now".
  const screenerRebuildLabel = useMemo(() => {
    if (!screenerEvidenceAsOf) return null;
    const ageMs = Date.now() - new Date(screenerEvidenceAsOf).getTime();
    if (!Number.isFinite(ageMs) || ageMs < 0) return null;
    const ageMin = Math.round(ageMs / 60000);
    const rel = ageMin < 1 ? "<1m ago" : ageMin < 60 ? `${ageMin}m ago` :
      ageMin < 1440 ? `${Math.round(ageMin / 60)}h ago` : `${Math.round(ageMin / 1440)}d ago`;
    const prov = screenerBuiltByProvider ? screenerBuiltByProvider.toUpperCase() : "—";
    return { rel, prov };
  }, [screenerEvidenceAsOf, screenerBuiltByProvider]);
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [pulseStrike, setPulseStrike] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedExpiry, setSelectedExpiry] = useState(() => {
    // Snapshot parity: a screener row forwards its exact expiry via `?expiry=`.
    // Seed state from the URL synchronously so the first data fetch already
    // prices that contract — no async race with the auto-select default.
    // Router state (Phase 2) takes priority since it carries the resolved
    // snapshot the user just clicked from.
    const stateExp = screenerSnapshot?.expiry;
    if (stateExp && /^\d{4}-\d{2}-\d{2}$/.test(stateExp)) return stateExp;
    const e = new URLSearchParams(window.location.search).get("expiry");
    return e && /^\d{4}-\d{2}-\d{2}$/.test(e) ? e : null;
  });
  // Ref mirror so async load callbacks never read a stale `selectedExpiry`
  // closure and stomp a URL-provided expiry with the default chip.
  const selectedExpiryRef = useRef(selectedExpiry);
  useEffect(() => { selectedExpiryRef.current = selectedExpiry; }, [selectedExpiry]);
  const missingPremiumRefreshRef = useRef({});
  const [refreshState, setRefreshState] = useState(null);
  const [refreshOutcome, setRefreshOutcome] = useState(null);
  const [refreshTimings, setRefreshTimings] = useState(null);

  const displayedExpiryRef = useRef(null);
  useEffect(() => { displayedExpiryRef.current = options?.selected_expiry || null; }, [options]);

  // Iter191b — UOA flag → Options Chain jump: switch expiry only if the chain
  // is displaying a different one, then scroll to the strike and pulse it.
  useEffect(() => {
    const onJump = async (e) => {
      const { strike, expiry } = e.detail || {};
      if (strike == null) return;
      if (expiry && displayedExpiryRef.current && expiry !== displayedExpiryRef.current) {
        setSelectedExpiry(expiry);
        try {
          const { data: n } = await api.get(`/ticker/${symbol}/options?expiry=${expiry}`);
          setOptions(n);
        } catch { /* ignore */ }
      }
      let tries = 0;
      const scroll = () => {
        const el = document.getElementById(`chain-row-${strike}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setPulseStrike(strike);
          setTimeout(() => setPulseStrike(null), 2500);
        } else if (++tries < 12) {
          setTimeout(scroll, 300);
        }
      };
      scroll();
    };
    window.addEventListener("ml-uoa-jump", onJump);
    return () => window.removeEventListener("ml-uoa-jump", onJump);
  }, [symbol]);
  const [payoffData, setPayoffData] = useState(null);
  const [ivHistory, setIvHistory] = useState([]);
  const [selectedWing, setSelectedWing] = useState("delta10");
  const [selectedCallWing, setSelectedCallWing] = useState("delta30");
  const [selectedBcsShort, setSelectedBcsShort] = useState("delta30");
  const [selectedBcsLong, setSelectedBcsLong] = useState("delta10");
  const [profile, setProfile] = useState("proposed");
  const [profileLoading, setProfileLoading] = useState(false);
  const strikeMode = "proposed"; // legacy /ticker/data param (engine toggle is separate, non-destructive)
  // 3-state Strike Engine toggle (Δ Regime-Scaled / Key Levels / P(NT)).
  // Default Δ Regime-Scaled; last-used mode persisted per ticker in session memory.
  const [engineMode, setEngineMode] = useState(() => _readStrikeModes()[symbol] || "delta");
  const [regimeCtx, setRegimeCtx] = useState(null);
  const [regimeCtxLoading, setRegimeCtxLoading] = useState(false);
  const [showSR, setShowSR] = useState(false);
  const [executingTrade, setExecutingTrade] = useState(false);
  const [regimeData, setRegimeData] = useState(null);
  const [regimeLoading, setRegimeLoading] = useState(false);
  const [regimeSuggestions, setRegimeSuggestions] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(() => parseStrategy(searchParams.get("strategy")));
  useEffect(() => {
    const fromUrl = parseStrategy(searchParams.get("strategy"));
    if (fromUrl) setSelectedStrategy(fromUrl);
  }, [symbol]);
  const [ivTermData, setIvTermData] = useState(null);
  const [ivTermOpen, setIvTermOpen] = useState(false);
  const [regimeDiffOpen, setRegimeDiffOpen] = useState(false);
  const [skewData, setSkewData] = useState(null);
  const [regimeBands, setRegimeBands] = useState([]);
  const [skewOpen, setSkewOpen] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  // Bubble-up of /regime/ranked-strategies regime so we can sanity-check parity
  // against /regime/evaluate (Smart Mode). Should always agree because both
  // endpoints use cached_infer_regime, but we warn on divergence to catch future drift.
  const [piRegime, setPiRegime] = useState(null);
  // P1.8/P1.9 — active data source banner + order ticket dialog
  const [activeSource, setActiveSource] = useState(null);
  const [orderTicket, setOrderTicket] = useState(null);
  // Tape view toggle — "hmm" shows the 3-strip Markov regime tape + override
  // markers; "technicals" shows a single rule-based Bull/Sideways/Bear bar.
  const [tapeMode, setTapeMode] = useState("hmm");

  useEffect(() => {
    api.get("/broker/active-source").then(({ data }) => setActiveSource(data)).catch(() => setActiveSource({ source: "yfinance" }));
    const onOpen = (e) => setOrderTicket(e.detail);
    window.addEventListener("markovlab:open-order-ticket", onOpen);
    return () => window.removeEventListener("markovlab:open-order-ticket", onOpen);
  }, []);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setUserSettings(data)).catch(() => {});
  }, []);

  // Strike Engine mode: hydrate from session memory on ticker change.
  // (Persistence happens synchronously in the toggle onClick — writing in an
  // effect keyed on [symbol] would clobber the restored value with the prior
  // ticker's stale engineMode before the reset setState applies.)
  useEffect(() => {
    // A3 — inherit the screener's short-leg philosophy via the `?mode=` query
    // param so the drill-down strikes match the row the user clicked. URL mode
    // wins over session memory; otherwise fall back to the last-used per ticker.
    const urlMode = searchParams.get("mode");
    const valid = ["delta", "key_levels", "pnt"];
    if (urlMode && valid.includes(urlMode)) {
      setEngineMode(urlMode);
      _writeStrikeMode(symbol, urlMode);
    } else {
      setEngineMode(_readStrikeModes()[symbol] || "delta");
    }
    // Snapshot parity — inherit the screener row's exact expiry via `?expiry=`
    // so the workbench prices the SAME contract the row showed (same DTE,
    // same chain) instead of re-snapping to its own default.
    const urlExpiry = searchParams.get("expiry");
    if (urlExpiry && /^\d{4}-\d{2}-\d{2}$/.test(urlExpiry)) {
      setSelectedExpiry(urlExpiry);
    }
  }, [symbol]);

  // P1.4 — load regime bands for the price chart. Triggers a backfill when
  // (a) history is empty, or (b) the most recent persisted date is older
  // than the last OHLCV trading bar shown in the chart, or (c) [iter179]
  // the existing rows are dominated by a single state (>90 %) across ≥30
  // bars — a stuck-corrupt signature that force-rebuild heals. Re-runs
  // whenever `symbol` changes or new OHLCV arrives.
  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    const lastBarDate = data?.ohlcv?.length ? data.ohlcv[data.ohlcv.length - 1].date : null;

    const stuckCorrupt = (rows) => {
      if (!rows || rows.length < 30) return false;
      const counts = {};
      rows.forEach(r => {
        const l = r.state_label;
        if (l) counts[l] = (counts[l] || 0) + 1;
      });
      const top = Math.max(0, ...Object.values(counts));
      return top / rows.length > 0.90;
    };

    const load = async () => {
      try {
        const { data: hist } = await api.get(`/regime/${symbol}/history?days=180`);
        if (cancelled) return;
        const rows = hist.history || [];
        const lastHistDate = rows.length ? rows[rows.length - 1].date : null;
        const dateStale = !rows.length || (lastBarDate && lastHistDate && lastHistDate < lastBarDate);
        const corrupt = stuckCorrupt(rows);
        if (!dateStale && !corrupt) {
          setRegimeBands(rows);
          return;
        }
        // Scheduled HMM batch writes history. Do not train on ticker open.
        if (!cancelled) setRegimeBands(rows);
      } catch {
        if (!cancelled) setRegimeBands([]);
      }
    };
    setRegimeBands([]);
    load();
    return () => { cancelled = true; };
  }, [symbol, data?.ohlcv?.length && data.ohlcv[data.ohlcv.length - 1]?.date]);

  useEffect(() => {
    const load = async () => {
      try {
        setProfileLoading(true);
        const stratParam = selectedStrategy ? `&strategy=${selectedStrategy}` : '';
        const smParam = `&strike_mode=${strikeMode}&strike_engine_mode=${engineMode}`;
        const expiryParam = selectedExpiry ? `&expiry=${selectedExpiry}` : '';
        const { data: tickerData } = await api.get(`/ticker/${symbol}/data?profile=${profile}${stratParam}${smParam}${expiryParam}`);
        setData(tickerData);
        // P1.8 — prefer the source actually used for THIS response
        if (tickerData.data_source) {
          setActiveSource(prev => ({ ...(prev || {}), source: tickerData.data_source }));
        }
        setLoading(false);
        setProfileLoading(false);

        const errs = tickerData.wing_scenarios?.validation_errors || [];
        const exp = selectedExpiry || tickerData.selected_expiry;
        const strat = selectedStrategy || tickerData.recommendation?.strategy || "";
        const missKey = `${symbol}|${exp}|${strat}|${engineMode}`;
        if (
          tickerData.wing_scenarios?.valid === false
          && errs.includes("missing_premium")
          && exp
          && !missingPremiumRefreshRef.current[missKey]
        ) {
          missingPremiumRefreshRef.current[missKey] = true;
          const q = new URLSearchParams();
          q.set("expiry", exp);
          if (strat) q.set("strategy", strat);
          q.set("force", "true");
          try {
            await api.post(`/ticker/${symbol}/option-snapshot/refresh?${q.toString()}`);
            const reload = async () => {
              const { data: again } = await api.get(`/ticker/${symbol}/data?profile=${profile}${stratParam}${smParam}${expiryParam}`);
              setData(again);
              if (again.data_source) {
                setActiveSource(prev => ({ ...(prev || {}), source: again.data_source }));
              }
              return again;
            };
            const again = await reload();
            const stillMissing = again.wing_scenarios?.valid === false
              && (again.wing_scenarios?.validation_errors || []).includes("missing_premium");
            if (stillMissing) {
              await new Promise((r) => setTimeout(r, 2500));
              await reload();
            }
          } catch {
            /* keep the invalid payload; do not loop */
          }
        }

        // Auto-select default expiry on first load only — never override a
        // URL-forwarded or user-picked expiry (checked via ref, not the closure).
        if (!selectedExpiryRef.current && tickerData.available_expirations?.length > 0) {
          const exps = tickerData.available_expirations;
          const green = exps.find(e => e.status === "green");
          const safe = exps.find(e => e.status !== "red");
          const defaultExp = green || safe || exps[0];
          if (defaultExp) setSelectedExpiry(defaultExp.date);
        }

        // DTE sync assertion (dev mode)
        if (tickerData.active_dte && selectedExpiry) {
          const chipExp = tickerData.available_expirations?.find(e => e.date === selectedExpiry);
          if (chipExp && Math.abs(chipExp.dte - tickerData.active_dte) > 1) {
            console.error(`[DTE SYNC CHECK] chip_dte=${chipExp.dte} | backend_dte=${tickerData.active_dte} — MISMATCH`);
          }
        }

        // ── Load supplementary data ASYNC (don't block render) ──
        api.get(`/ticker/${symbol}/options`).then(({ data: optionsData }) => setOptions(optionsData)).catch(() => {});
        api.get(`/ticker/${symbol}/iv-history`).then(({ data: ivData }) => setIvHistory(ivData.history || [])).catch(() => {});
        api.get(`/ticker/${symbol}/iv-term-structure`).then(({ data: termData }) => setIvTermData(termData)).catch(() => {});
        api.get(`/ticker/${symbol}/skew`).then(({ data: sk }) => setSkewData(sk)).catch(() => {});
      } catch (err) {
        console.error("Ticker data error:", err);
        setLoading(false);
        setProfileLoading(false);
      }
    };
    load();
  }, [symbol, profile, selectedStrategy, strikeMode, engineMode, selectedExpiry]);

  useEffect(() => {
    if (!symbol || !data?.daily_bar?.needs_priority) return undefined;
    let n = 0;
    const id = setInterval(async () => {
      n += 1;
      if (n > 12) {
        clearInterval(id);
        return;
      }
      try {
        const stratParam = selectedStrategy ? `&strategy=${selectedStrategy}` : "";
        const smParam = `&strike_mode=${strikeMode}&strike_engine_mode=${engineMode}`;
        const expiryParam = selectedExpiry ? `&expiry=${selectedExpiry}` : "";
        const { data: again } = await api.get(
          `/ticker/${symbol}/data?profile=${profile}${stratParam}${smParam}${expiryParam}`,
        );
        setData(again);
        if (!again?.daily_bar?.needs_priority) clearInterval(id);
      } catch {
        /* keep showing the lagged cached bars */
      }
    }, 45000);
    return () => clearInterval(id);
  }, [symbol, data?.daily_bar?.needs_priority, profile, selectedStrategy, strikeMode, engineMode, selectedExpiry]);

  // Regime Backtest Context (shared with the Evidence Verdict banner + table).
  // Fetched once at the parent and passed down so we never double-call.
  useEffect(() => {
    const strat = data?.recommendation?.strategy;
    const PREMIUM = ["bull_put_spread", "cash_secured_put", "short_put", "covered_call"];
    if (!symbol || !PREMIUM.includes(strat)) { setRegimeCtx(null); return; }
    let cancelled = false;
    setRegimeCtxLoading(true);
    api.get(`/ticker/${symbol}/regime-backtest-context`, { params: { strategy: strat, dte: data?.active_dte || 30 } })
      .then(({ data: d }) => { if (!cancelled) setRegimeCtx(d); })
      .catch(() => { if (!cancelled) setRegimeCtx(null); })
      .finally(() => { if (!cancelled) setRegimeCtxLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, data?.recommendation?.strategy, data?.active_dte]);


  // Load payoff diagram — leg-based computation from wing_scenarios
  useEffect(() => {
    if (!data?.recommendation?.strategy) return;
    const rec = data.recommendation;
    const sig = data.signals;
    const ws = data.wing_scenarios;
    if (ws?.valid === false || data?.recommendation?.tradeable === false) {
      setPayoffData(null);
      return;
    }
    const spot = data.quote?.price || data?.current_price || sig?.current_price || 0;
    if (!spot) return;

    // Build legs array from wing_scenarios
    const legs = [];
    if (ws?.legs) {
      for (const leg of ws.legs) {
        const label = (leg.label || "").toLowerCase();
        const strike = leg.strike || 0;
        const prem = leg.premium || 0;
        let legType = "";
        if (label.includes("long call") || (label.includes("buy") && label.includes("call"))) legType = "long_call";
        else if (label.includes("short call") || (label.includes("sell") && label.includes("call"))) legType = "short_call";
        else if (label.includes("long put") || (label.includes("buy") && label.includes("put"))) legType = "long_put";
        else if (label.includes("short put") || (label.includes("sell") && label.includes("put"))) legType = "short_put";
        else if (label.includes("long stock")) legType = "long_stock";
        else if (label.includes("short stock")) legType = "short_stock";
        if (legType && strike > 0) {
          legs.push({ type: legType, strike, premium: prem, qty: 1, label: leg.label });
        }
      }
    }

    // Do not invent premiums. Payoff only from validated observed legs.
    if (legs.length === 0) {
      setPayoffData(null);
      return;
    }

    if (legs.length === 0) return;
    const body = {
      strategy_type: rec.strategy, current_price: spot, contracts: 1, legs,
      input_quality: ws?.input_quality || data?.option_freshness?.status,
      input_quality_label: ws?.input_quality_label || data?.option_freshness?.user_label,
    };
    api.post("/payoff/calculate", body).then(({ data: pd }) => setPayoffData(pd)).catch(() => {});
  }, [data]);

  // Load regime suitability — always (dual panel, not toggleable)
  useEffect(() => {
    if (!symbol || !data?.recommendation?.strategy) return;
    setRegimeLoading(true);
    api.post("/regime/evaluate", {
      ticker: symbol,
      strategy: data.recommendation.strategy,
      dte: data?.active_dte || 30,
    })
      .then(({ data: d }) => setRegimeData(d))
      .catch(() => setRegimeData(null))
      .finally(() => setRegimeLoading(false));
  }, [symbol, data?.recommendation?.strategy, data?.active_dte]);

  // Regime parity sanity check — /regime/evaluate (Smart Mode) and
  // /regime/ranked-strategies (Position Intelligence) MUST agree on regime.state
  // because both call cached_infer_regime. If they ever diverge it means the
  // cache layer or model version drifted between the two reads — surface a
  // console.warn so we catch it early. Resets per symbol.
  useEffect(() => {
    const evalState = regimeData?.regime?.state;
    const piState = piRegime?.state;
    if (!symbol || !evalState || !piState) return;
    if (evalState !== piState) {
      console.warn(
        `[regime-parity] ${symbol}: /regime/evaluate=${evalState} vs /regime/ranked-strategies=${piState}. ` +
        `Both endpoints share cached_infer_regime — divergence indicates a cache or model-version drift.`
      );
    }
  }, [symbol, regimeData?.regime?.state, piRegime?.state]);

  const handleSuggestRegime = async () => {
    setSuggestLoading(true);
    try {
      const { data: d } = await api.post("/regime/suggest", { ticker: symbol, dte: data?.active_dte || 30 });
      setRegimeSuggestions(d.suggestions || []);
    } catch (e) { console.warn(e); setRegimeSuggestions([]); }
    finally { setSuggestLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full p-8">
      <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />
    </div>
  );

  const sig = data?.signals || {};
  const rec = data?.recommendation || {};
  const earn = data?.earnings || {};
  const fund = data?.fundamentals || {};
  const quote = data?.quote || {};
  const headerPx = Number(quote.price || data?.current_price || 0);
  const sma50 = Number(sig.sma_50 || 0);
  const headerUp = sma50 ? headerPx > sma50 : true;

  // Compute snapped expiry for current profile (used by PI earnings check)
  const snappedExpiry = selectedExpiry;

  // Prepare chart data — downsample to last 120 days for performance
  const allOhlcv = (data?.ohlcv || []).filter((d) => !d?.quote_session);
  const allClose = allOhlcv.map(x => x.close);

  // EMA helper — recursive smoothing factor 2/(n+1). Returns array same length
  // as `values` with leading nulls until the EMA is initialised.
  const ema = (values, period) => {
    const k = 2 / (period + 1);
    const out = new Array(values.length).fill(null);
    let acc = null;
    for (let i = 0; i < values.length; i++) {
      const v = values[i];
      if (v == null || Number.isNaN(v)) continue;
      acc = acc == null ? v : v * k + acc * (1 - k);
      if (i >= period - 1) out[i] = acc;
    }
    return out;
  };

  // MACD = EMA(12) − EMA(26); signal = EMA(9) of MACD; histogram = MACD − signal.
  const ema12 = ema(allClose, 12);
  const ema26 = ema(allClose, 26);
  const macdLine = allClose.map((_, i) =>
    (ema12[i] != null && ema26[i] != null) ? ema12[i] - ema26[i] : null
  );
  const signalLine = ema(macdLine.map(v => v ?? 0), 9);
  const macdHist = macdLine.map((m, i) =>
    (m != null && signalLine[i] != null) ? m - signalLine[i] : null
  );

  // Classify each bar's technical trend per the documented rule set.
  // Bullish:  close > SMA50 > SMA200 AND MACD histogram ≥ 0
  // Bearish:  close < SMA50 < SMA200 AND MACD histogram ≤ 0
  // Sideways: any mixed signal, or SMA200 unavailable (first ~200 bars).
  const classifyTrend = (close, sma50, sma200, hist) => {
    if (sma50 == null || sma200 == null || hist == null) return "sideways";
    if (close > sma50 && sma50 > sma200 && hist >= 0) return "bullish";
    if (close < sma50 && sma50 < sma200 && hist <= 0) return "bearish";
    return "sideways";
  };

  const chartData = allOhlcv.slice(-120).map((d, i) => {
    const idx = allOhlcv.length - 120 + i;
    const sma50 = idx >= 49 ? allClose.slice(idx - 49, idx + 1).reduce((a, b) => a + b, 0) / 50 : null;
    const sma200 = idx >= 199 ? allClose.slice(idx - 199, idx + 1).reduce((a, b) => a + b, 0) / 200 : null;
    const hist = macdHist[idx];
    return {
      date: d.date.slice(5), // MM-DD format
      fullDate: d.date,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
      volume: d.volume,
      // For area chart: show the price range
      priceRange: [d.low, d.high],
      sma50: sma50 ? parseFloat(sma50.toFixed(2)) : null,
      sma200: sma200 ? parseFloat(sma200.toFixed(2)) : null,
      macd_hist: hist != null ? parseFloat(hist.toFixed(4)) : null,
      trend: classifyTrend(d.close, sma50, sma200, hist),
      color: d.close >= d.open ? "var(--hoip-profit)" : "var(--hoip-loss)",
      isUp: d.close >= d.open,
      quoteSession: Boolean(d.quote_session),
    };
  });

  const loadCachedOptions = (exp) => {
    const q = exp ? `?expiry=${encodeURIComponent(exp)}` : "";
    api.get(`/ticker/${symbol}/options${q}`)
      .then(({ data: n }) => { if (n) setOptions(n); })
      .catch(() => {});
  };

  const refreshOptionSnapshot = (exp) => {
    const q = new URLSearchParams();
    if (exp) q.set("expiry", exp);
    if (selectedStrategy) q.set("strategy", selectedStrategy);
    else if (data?.recommendation?.strategy) q.set("strategy", data.recommendation.strategy);
    q.set("force", "true");
    const qs = q.toString();
    const stratParam = selectedStrategy ? `&strategy=${selectedStrategy}` : "";
    const smParam = `&strike_mode=${strikeMode}&strike_engine_mode=${engineMode}`;
    const expiryParam = exp ? `&expiry=${exp}` : (selectedExpiry ? `&expiry=${selectedExpiry}` : "");
    setRefreshState("running");
    setRefreshOutcome(null);
    api.post(`/ticker/${symbol}/option-snapshot/refresh${qs ? `?${qs}` : ""}`)
      .then(async ({ data: n }) => {
        if (n) setOptions(n);
        const outcome = n?.refresh_outcome || (n?.refresh_queued ? "queued" : null);
        setRefreshOutcome(outcome);
        setRefreshTimings(n?.provider_timings || null);
        setRefreshState(n?.refresh_queued ? "queued" : "complete");
        try {
          const { data: tickerData } = await api.get(
            `/ticker/${symbol}/data?profile=${profile}${stratParam}${smParam}${expiryParam}`,
          );
          setData(tickerData);
        } catch {
          /* snapshot POST already applied; keep current workbench payload */
        }
      })
      .catch(() => {
        setRefreshState("failed");
        setRefreshOutcome("provider_unavailable");
      });
  };

  const layerStatus = data?.layer_status || {};
  const optionReadiness = data?.option_readiness || {};
  const regimeReadiness = data?.regime_readiness || {};
  const strategyReady = data?.wing_scenarios?.actionable === true || (
    data?.recommendation?.tradeable === true
    && data?.wing_scenarios?.valid !== false
    && data?.wing_scenarios?.blocks_selection !== true
  );
  const layerChip = (key, label) => {
    const s = layerStatus[key] || "queued";
    const ready = s === "ready";
    return (
      <span key={key} className="px-1.5 py-0.5"
        style={{ color: "var(--hoip-text-tertiary)" }}
        title={`${label}: ${s}`}
        data-testid={`layer-chip-${key}`}>{ready ? label : `${label}: ${s}`}</span>
    );
  };

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4" data-testid="ticker-workbench">
      {/* Header */}
      <div className="flex items-start gap-2 md:gap-3 flex-wrap">
        <div className="flex items-center gap-2 md:gap-3 flex-wrap flex-1 min-w-0">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-8 px-2" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-lg md:text-2xl font-bold tracking-tight font-mono-data">{symbol}</h1>
        <span className={`text-base md:text-xl font-mono-data font-medium ${headerUp ? "text-emerald-500" : "text-red-500"}`}>
          {headerPx > 0 ? `$${headerPx.toFixed(2)}` : "—"}
        </span>
        {quote.label && headerPx > 0 && (
          <span className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}
            data-testid="quote-session-label">
            {quote.display || quote.label}
          </span>
        )}
        {data?.ohlcv_close?.bar_date && data?.ohlcv_close?.close != null && (
          <span className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}
            data-testid="ohlcv-close-label">
            Daily close ${Number(data.ohlcv_close.close).toFixed(2)} · {data.ohlcv_close.bar_date}
          </span>
        )}
        {sig.trend && (
          <span className={`text-[10px] md:text-xs px-1.5 py-0.5 border font-mono-data ${sig.trend.includes("bull") ? "text-emerald-500 border-emerald-800" : sig.trend.includes("bear") ? "text-red-500 border-red-800" : "text-[var(--hoip-text-tertiary)] border-[var(--hoip-border)]"}`}>
            {sig.trend.replace(/_/g, " ")}
          </span>
        )}
        <span className="hidden sm:inline text-xs font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>Tech: {sig.technical_score ?? "—"}/10</span>
        <span className="hidden sm:inline text-xs font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>Fund: {fund.fundamental_score ?? "—"}/10</span>
        {(data?.freshness?.env || activeSource?.env) && (
          <span data-testid="ticker-env-badge" className="text-[10px] tracking-widest font-bold px-1.5 py-0.5"
            style={{ background: "var(--hoip-hover)", color: "var(--hoip-text-tertiary)" }}>
            {String(data?.freshness?.env || activeSource.env).toUpperCase()}
          </span>
        )}
        {/* Dev-only API audit chip — admin sees live vendor call counts for this ticker */}
        <span className="relative">
          <ApiAuditChip symbol={symbol} />
        </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-[10px] uppercase tracking-wide"
            style={{ color: "var(--hoip-text-tertiary)" }}
            data-testid="strategy-tools-label"
          >
            Strategy Tools
          </span>
          <Link
            to={intelligencePath(symbol, {
              expiry: selectedExpiry,
              dte: parseDte(searchParams.get("dte")),
              family: parseFamily(searchParams.get("family"))
                || parseFamily(typeof sessionStorage !== "undefined" ? sessionStorage.getItem("ml_family_filter") : null),
              strategy: parseStrategy(selectedStrategy),
            }, {
              expirations: data?.available_expirations,
              asOfDate: data?.ohlcv_close?.bar_date
                || (data?.ohlcv || []).filter((d) => !d?.quote_session).slice(-1)[0]?.date,
            })}
            className="text-[11px] underline"
            style={{ color: "var(--hoip-text-secondary)" }}
            data-testid="open-intelligence-workspace"
          >
            Open Intelligence Workspace
          </Link>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 text-[10px] font-mono-data" data-testid="layer-status-chips">
        {layerChip("chart", "Chart")}
        {layerChip("technicals", "Technicals")}
        {data?.daily_bar?.status && data.daily_bar.status !== "current" && data.daily_bar.last_bar_date && (
          <span className="px-1.5 py-0.5" data-testid="daily-bar-lag"
            style={{ color: "var(--hoip-text-tertiary)" }}
            title={`Daily bars through ${data.daily_bar.last_bar_date}`}>
            Daily bars through {data.daily_bar.last_bar_date}
          </span>
        )}
        <span className="px-1.5 py-0.5" data-testid="layer-chip-regime"
          style={{ color: "var(--hoip-text-tertiary)" }}
          title={regimeReadiness.summary || undefined}>
          {regimeReadiness.latest_inference === "ready"
            ? "Regime ready"
            : (regimeReadiness.history === "available" ? "Regime updating" : `Regime: ${layerStatus.regime || "queued"}`)}
        </span>
        {(() => {
          const st = workbenchOptionStatus(optionReadiness, data?.option_freshness || {});
          return (
            <StatusBadge
              testid="workbench-option-status"
              tier={st.tier}
              text={st.text}
              title={st.full || optionReadiness.summary || st.text}
            />
          );
        })()}
        <span className="sr-only" data-testid="layer-chip-structure">
          {optionReadiness.structurally_valid ? "Structurally valid" : "Structure preparing"}
        </span>
        <span className="sr-only" data-testid="layer-chip-quote">
          {optionReadiness.quote_complete
            ? (optionReadiness.indicative_only ? "Quote indicative only" : "Quote complete")
            : "Quote incomplete"}
        </span>
        <span className="sr-only" data-testid="layer-chip-trade-elig">
          {optionReadiness.trade_selection_eligible && !optionReadiness.freshness_blocks && !data?.option_freshness?.blocks_selection
            ? "Trade selection eligible"
            : "Trade selection blocked"}
        </span>
        <span className="sr-only" data-testid="layer-chip-chain">
          {optionReadiness.summary || "Options preparing"}
        </span>
        {optionReadiness.csp_vs_spread ? (
          <span data-testid="csp-vs-spread-note" style={{ color: "var(--hoip-text-tertiary)" }}>{optionReadiness.csp_vs_spread}</span>
        ) : null}
        {data?.option_freshness?.user_label || data?.option_freshness?.option_snapshot_fetched_at || data?.option_freshness?.fetched_at ? (
          <div className="text-[10px] font-mono-data space-y-0.5 w-full" style={{ color: "var(--hoip-text-tertiary)" }}
            data-testid="option-freshness-summary">
            <details data-testid="option-freshness-detail">
              <summary className="cursor-pointer" style={{ color: "var(--hoip-text-tertiary)" }}>Snapshot times and provider</summary>
              <div>Option snapshot fetched: {data.option_freshness.fetched_at_et || data.option_freshness.option_snapshot_fetched_at || data.option_freshness.fetched_at || "—"}</div>
              <div>Option quote time: {data.option_freshness.option_quote_as_of_et || (data.option_freshness.quote_timestamp_quality === "verified" ? data.option_freshness.option_quote_as_of : "unknown")}</div>
              <div>Last trade: {data.option_freshness.option_last_trade_at_et || data.option_freshness.option_last_trade_at || "—"}</div>
              <div>
                Age: {data.option_freshness.age || "n/a (no verified option quote time)"}
                {" · "}Status: {data.option_freshness.user_label || "—"}
                {" · "}Timestamp quality: {data.option_freshness.quote_timestamp_quality || "unknown"}
              </div>
              <div>
                Underlying quote: {data.option_freshness.underlying_quote_as_of_et || data.option_freshness.underlying_quote_as_of || "—"}
                {data.option_freshness.observation_spot != null ? ` · spot at obs $${Number(data.option_freshness.observation_spot).toFixed(2)}` : ""}
                {data.option_freshness.current_spot != null ? ` · current $${Number(data.option_freshness.current_spot).toFixed(2)}` : ""}
              </div>
              <div>Provider: {data.option_freshness.provider || "—"}</div>
              <div>Pricing method: {data.option_freshness.pricing_method || "—"}</div>
              <div>Snapshot version: {data.option_freshness.snapshot_version || "—"}</div>
              <div>Fetched UTC: {data.option_freshness.fetched_at_utc || data.option_freshness.fetched_at || "—"}</div>
            </details>
            {(refreshOutcome || refreshState || options?.refresh_report) && (
              <div data-testid="option-refresh-outcome">
                Refresh: {refreshState || "—"}{refreshOutcome ? ` · ${String(refreshOutcome).replace(/_/g, " ")}` : ""}
                {options?.refresh_report?.ticker ? ` · ${options.refresh_report.ticker}` : ""}
                {options?.refresh_report?.expiry ? ` · ${String(options.refresh_report.expiry).slice(0, 10)}` : ""}
                {Array.isArray(options?.refresh_report?.providers_tried) && options.refresh_report.providers_tried.length
                  ? ` · providers ${options.refresh_report.providers_tried.join(", ")}` : ""}
                {options?.refresh_report?.option_quote_as_of ? ` · quote ${options.refresh_report.option_quote_as_of}` : ""}
                {options?.refresh_report?.option_snapshot_fetched_at ? ` · fetched ${options.refresh_report.option_snapshot_fetched_at}` : ""}
                {options?.refresh_report?.quote_timestamp_quality ? ` · quality ${options.refresh_report.quote_timestamp_quality}` : ""}
                {refreshOutcome === "rate_limited" && options?.retry_after_sec ? ` · retry in ${options.retry_after_sec}s` : ""}
                {refreshTimings && Object.keys(refreshTimings).length ? (
                  <span> · {Object.entries(refreshTimings).map(([k, v]) => `${k.replace(/_ms$/, "")} ${v}ms`).join(" · ")}</span>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* DTE Timeline + Intelligence */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,68%)_minmax(0,32%)] gap-3 items-start">
        <div className="border p-3 rounded-sm space-y-2 min-w-0" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <h3 className="text-[10px] font-bold mb-1" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-secondary)" }}>DTE Timeline</h3>
        {data?.expiry_policy?.banner ? (
          <div className="text-[10px] mb-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="workbench-expiry-policy">
            {data.expiry_policy.banner}
          </div>
        ) : null}
        {!(data?.available_expirations || []).length ? (
          <LayerProgress
            progress={data?.prepare_status?.chain}
            fallback={data?.user_status || (data?.prepare_status?.chain?.ahead != null
              ? `Expiration snapshot preparing — queue position ${data.prepare_status.chain.ahead}; estimated availability about ${data.prepare_status.chain.eta_minutes_low || "—"}–${data.prepare_status.chain.eta_minutes_high || "—"} minutes.`
              : "Expiration snapshot preparing")}
          />
        ) : null}
        <DTETimeline dteProfiles={data?.dte_profiles} activeProfile={profile}
          daysToEarnings={earn.days_to_earnings}
          availableExpirations={data?.available_expirations}
          earningsDate={earn.next_earnings_date}
          selectedExpiry={selectedExpiry}
          earningsPassed={earn.earnings_passed}
          onSelectExpiry={(exp) => { setSelectedExpiry(exp); loadCachedOptions(exp); }}
          preparingCopy={data?.user_status || (data?.prepare_status?.chain?.ahead != null
            ? `Expiration snapshot preparing — queue position ${data.prepare_status.chain.ahead}; estimated availability about ${data.prepare_status.chain.eta_minutes_low || "—"}–${data.prepare_status.chain.eta_minutes_high || "—"} minutes.`
            : "Expiration snapshot preparing")} />
        <ExpiryChips expirations={data?.available_expirations}
          selectedExpiry={selectedExpiry} setSelectedExpiry={(exp) => { setSelectedExpiry(exp); loadCachedOptions(exp); }}
          bestEntry={data?.best_entry}
          regimeP75={data?.regime_p75 || regimeData?.regime?.sojourn_p75}
          regimeAllowsPremium={data?.regime_allows_premium} />
        <button type="button" onClick={() => refreshOptionSnapshot(selectedExpiry)}
          className="text-[10px] underline" style={{ color: "var(--hoip-text-tertiary)" }}
          title="Explicit ticker+listed-expiry compact refresh. A new fetched_at or snapshot version is not by itself a refresh of option quotes."
          data-testid="refresh-option-snapshot"
          disabled={refreshState === "running"}>
          {refreshState === "running" ? "Refreshing option snapshot…" : "Refresh option snapshot"}
        </button>
        </div>
        <div className="min-w-0">
          <IntelStrip symbol={symbol} />
        </div>
      </div>

      {/* Position Intelligence */}
      <PositionIntelligence symbol={symbol} positionContext={data?.position_context} rec={rec}
        onSelectStrategy={setSelectedStrategy}
        onUserSelectStrategy={(id) => { setSelectedStrategy(id); loadCachedOptions(selectedExpiryRef.current); }}
        selectedStrategy={selectedStrategy}
        selectedExpiry={snappedExpiry} onRegimeLoaded={setPiRegime} />

      {/* Price Chart — Candlestick via custom SVG */}
      <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-xs font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Price Chart (120D)</h2>
            {/* Tape mode toggle — Regime · HMM ↔ Trend · Technicals.
                Default is HMM (probabilistic regime tape). Switching to
                Technicals replaces the 3-strip tape with a single rule-based
                Bull/Sideways/Bear bar; the mini HMM / Tech cards on the right
                of the workbench are unaffected. */}
            <div className="inline-flex border rounded-sm overflow-hidden text-[9px] font-bold tracking-wide"
              style={{ borderColor: "var(--hoip-border)" }}
              data-testid="tape-mode-toggle"
              role="group" aria-label="Chart tape mode">
              <button
                type="button"
                onClick={() => setTapeMode("hmm")}
                className="px-2 py-0.5 transition-colors"
                style={{
                  background: tapeMode === "hmm" ? "var(--hoip-text-primary)" : "transparent",
                  color: tapeMode === "hmm" ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                }}
                title="HMM regime tape — 5-day majority + instantaneous daily ribbon."
                data-testid="tape-mode-hmm">
                Regime · HMM
              </button>
              <button
                type="button"
                onClick={() => setTapeMode("technicals")}
                className="px-2 py-0.5 transition-colors"
                style={{
                  background: tapeMode === "technicals" ? "var(--hoip-text-primary)" : "transparent",
                  color: tapeMode === "technicals" ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                  borderLeft: "1px solid var(--hoip-border)",
                }}
                title="Rule-based trend tape — Bullish if close > SMA50 > SMA200 AND MACD hist ≥ 0; Bearish if reverse; Sideways otherwise."
                data-testid="tape-mode-technicals">
                Trend · Technicals
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 inline-block"></span>SMA50</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-yellow-500 inline-block"></span>SMA200</span>
            {tapeMode === "hmm" && regimeBands.length > 0 && (
              <span className="flex items-center gap-1 flex-wrap" data-testid="regime-legend">
                <span className="text-[8px] tracking-widest opacity-60">REGIME</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2" style={{background:'#0ea5e9'}}></span>Quiet</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2" style={{background:'#10b981'}}></span>Bull</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2" style={{background:'#ef4444'}}></span>Bear</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2" style={{background:'#f59e0b'}}></span>Vol</span>
                {regimeBands.some(r => r.label_override) && (
                  <span className="flex items-center gap-1"
                    title="HMM label was overridden by the tech-vs-regime sanity layer on this bar — hover the chart to see the reason."
                    data-testid="regime-legend-override">
                    <span className="inline-flex items-center justify-center text-[#EAB308] text-[10px] font-bold" style={{lineHeight:1}}>▼</span>
                    Override
                  </span>
                )}
              </span>
            )}
            {tapeMode === "hmm" && regimeBands.length > 0 && (() => {
              const lastBand = regimeBands[regimeBands.length - 1]?.date || "";
              const lastBar = chartData?.length ? (chartData[chartData.length - 1]?.fullDate || "") : "";
              const expected = data?.daily_bar?.last_completed_market_session || "";
              const hmmThrough = data?.hmm_result_as_of || data?.regime_readiness?.history_through || lastBand;
              return (hmmThrough && ((lastBar && hmmThrough < lastBar) || (expected && hmmThrough < expected))) && (
                <span className="px-1.5 py-0.5 text-[9px] cursor-help" data-testid="regime-pending-today"
                  style={{ color: "var(--hoip-text-tertiary)" }}
                  title="HMM follows cached daily bars, not the header quote. This updates after the daily bar is stored.">
                  HMM through {hmmThrough}{expected && expected !== hmmThrough ? ` · session ${expected}` : ""}
                </span>
              );
            })()}
            {tapeMode === "technicals" && (
              <span className="flex items-center gap-1 flex-wrap" data-testid="tech-legend"
                title="Per-bar trend rule: Bullish if close > SMA50 > SMA200 AND MACD histogram ≥ 0; Bearish if close < SMA50 < SMA200 AND histogram ≤ 0; Sideways otherwise (incl. SMA200 unavailable).">
                <span className="text-[8px] tracking-widest opacity-60">TREND</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2" style={{background:'#10b981'}}></span>Bull</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2" style={{background:'#71717A'}}></span>Sideways</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2 h-2" style={{background:'#ef4444'}}></span>Bear</span>
              </span>
            )}
            <button data-testid="sr-toggle" onClick={() => setShowSR(!showSR)}
              className={`px-1.5 py-0.5 border rounded-sm text-[9px] font-bold ${showSR ? "bg-[var(--hoip-text-primary)] text-[var(--hoip-bg)] border-[var(--hoip-text-primary)]" : "border-[var(--hoip-border)]"}`}>
              S/R
            </button>
          </div>
        </div>
        <div className="relative">
          <CandlestickChart data={chartData} regimeBands={regimeBands} tapeMode={tapeMode}
            emptyMessage={data?.layer_status?.chart === "ready" ? "No chart data" : (
              <LayerProgress progress={data?.prepare_status?.ohlcv}
                fallback="Queued for initial history; timing will update when the next worker starts." />
            )} />
          {data?.quote_overlay?.price && chartData.length > 0 && (
            <div className="absolute inset-0 pointer-events-none" style={{ marginLeft: 50, marginRight: 30 }}
              data-testid="quote-overlay">
              {(() => {
                const allPrices = chartData.map(d => d.high).concat(chartData.map(d => d.low));
                allPrices.push(Number(data.quote_overlay.price));
                const minP = Math.min(...allPrices);
                const maxP = Math.max(...allPrices);
                const range = maxP - minP || 1;
                const y = `${100 - ((Number(data.quote_overlay.price) - minP) / range) * 100}%`;
                return (
                  <div className="absolute w-full flex items-center" style={{ top: y }}>
                    <div className="flex-1 border-t border-dotted" style={{ borderColor: "#60A5FA", opacity: 0.7 }} />
                    <span className="text-[8px] font-mono-data ml-1 whitespace-nowrap" style={{ color: "#60A5FA" }}>
                      Quote ${Number(data.quote_overlay.price).toFixed(2)}
                    </span>
                  </div>
                );
              })()}
            </div>
          )}
          {showSR && data?.key_levels && (
            <div className="absolute inset-0 pointer-events-none" style={{ marginLeft: 50, marginRight: 30 }}>
              {(() => {
                const allPrices = chartData.map(d => d.high).concat(chartData.map(d => d.low));
                const minP = Math.min(...allPrices);
                const maxP = Math.max(...allPrices);
                const range = maxP - minP || 1;
                const pctY = (p) => `${100 - ((p - minP) / range) * 100}%`;
                const levels = [];
                (data.key_levels.resistance || []).forEach(r => levels.push({ ...r, color: "#EF4444", type: "R" }));
                (data.key_levels.support || []).forEach(s => levels.push({ ...s, color: "#10B981", type: "S" }));
                // Strategy strike lines
                const strikes = rec.strikes || {};
                Object.values(strikes).forEach(v => {
                  if (typeof v === "number" && v > minP && v < maxP) levels.push({ level: v, label: "Strike", color: "#F59E0B", type: "K" });
                });
                return levels.filter(l => l.level > minP && l.level < maxP).map((l, i) => (
                  <div key={i} className="absolute w-full flex items-center" style={{ top: pctY(l.level) }}>
                    <div className="flex-1 border-t" style={{ borderColor: l.color, borderStyle: l.type === "K" ? "dotted" : "dashed", opacity: l.type === "K" ? 0.6 : 0.5 }} />
                    <span className="text-[8px] font-mono-data ml-1 whitespace-nowrap" style={{ color: l.color }}>
                      {l.type !== "K" && `${l.label} `}${l.level}
                    </span>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
        {/* Volume mini chart */}
        <ResponsiveContainer width="100%" height={50}>
          <ComposedChart data={chartData} margin={{ top: 0, right: 30, bottom: 0, left: 50 }}>
            <XAxis dataKey="date" hide />
            <YAxis hide domain={[0, 'auto']} />
            <Bar dataKey="volume" barSize={3}>
              {chartData.map((d, i) => (
                <Cell key={i} fill={d.isUp ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="border rounded-sm" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="regime-diff-collapsible">
        <button onClick={() => setRegimeDiffOpen(!regimeDiffOpen)} className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-bold cursor-pointer hover:bg-[var(--hoip-hover)]"
          style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }} data-testid="regime-diff-toggle">
          <span>Regime Diff: 5D vs Instant</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${regimeDiffOpen ? "rotate-180" : ""}`} style={{ color: "var(--hoip-text-tertiary)" }} />
        </button>
        {regimeDiffOpen && (
          <div className="px-2 pb-3">
            <RegimeDiffChart symbol={symbol} days={60}
              hideFrame
              intradayHint={(() => {
                const trend = sig?.trend;
                if (!trend) return null;
                const lean = trend.charAt(0).toUpperCase() + trend.slice(1);
                const tech = sig.technical_score != null ? `Tech ${sig.technical_score}/10` : null;
                const rsi = sig.rsi_14 != null ? `RSI ${Math.round(sig.rsi_14)}` : null;
                const detail = [tech, rsi].filter(Boolean).join(", ");
                return `intraday signal leans ${lean}${detail ? ` (${detail})` : ""}`;
              })()} />
          </div>
        )}
      </div>

      {/* Dual Signal Panel — Tech left, Smart Mode right */}
      <DualSignalPanel sig={sig} fund={fund} earn={earn}
        regimeData={regimeData} regimeLoading={regimeLoading}
        strategy={rec.strategy} keyLevels={data?.key_levels} />

      {/* Pre-market overlay banner */}
      {data?.regime_output?.premarket_overlay?.stress && (
        <div className="flex items-start gap-2 px-3 py-2 border text-[10px]"
          style={{ background: "rgba(245,158,11,0.06)", borderColor: "rgba(245,158,11,0.25)", color: "#F59E0B" }}
          data-testid="premarket-overlay-banner">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-bold">Pre-market overlay active</span>
            <span className="ml-2 font-mono-data">Gap: {data.regime_output.premarket_overlay.gap_pct > 0 ? "+" : ""}{data.regime_output.premarket_overlay.gap_pct}%</span>
            {data.regime_output.effective_posture && (
              <span className="ml-2">
                Core: <span className="font-bold" style={{ color: stateColors[data.regime_output.dominant_state_label] }}>{data.regime_output.dominant_state_label}</span>
                {" → "}Opening posture: <span className="font-bold">{data.regime_output.effective_posture}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Strategy Analysis — FULL WIDTH */}
      <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Strategy Analysis</h2>
        </div>

          {/* Evidence Verdict — synthesizes the regime backtest numbers into a
              single verdict. Replaces the binary regime-label "DANGER" banner. */}
          {(() => {
            const PREMIUM = ["bull_put_spread", "cash_secured_put", "short_put", "covered_call"];
            if (!rec.strategy || !PREMIUM.includes(rec.strategy)) return null;
            const v = regimeCtx?.verdict;
            const tone = v?.verdict || "neutral";
            const toneStyle = {
              green: { c: "#10B981", bg: "rgba(16,185,129,0.08)", b: "rgba(16,185,129,0.4)" },
              amber: { c: "#F59E0B", bg: "rgba(245,158,11,0.08)", b: "rgba(245,158,11,0.4)" },
              red: { c: "#EF4444", bg: "rgba(239,68,68,0.08)", b: "rgba(239,68,68,0.4)" },
              neutral: { c: "var(--hoip-text-tertiary)", bg: "var(--hoip-hover)", b: "var(--hoip-border)" },
            }[tone];
            const ageBars = regimeData?.regime?.sojourn_days != null ? `${regimeData.regime.sojourn_days.toFixed(0)}d in` : null;
            const conf = regimeData?.regime?.prob != null ? `${(regimeData.regime.prob * 100).toFixed(0)}% confidence` : null;
            const subtitle = [regimeData?.regime?.state, conf, ageBars].filter(Boolean).join(" · ");
            return (
              <div data-testid="evidence-verdict-banner"
                className="mb-3 px-3 py-2 border rounded-sm"
                style={{ borderColor: toneStyle.b, background: toneStyle.bg }}>
                <div className="flex items-center gap-2">
                  {tone === "green" ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: toneStyle.c }} />
                    : <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: toneStyle.c }} />}
                  <span className="text-[11px] font-bold tracking-wide" data-testid="evidence-verdict-headline" style={{ color: toneStyle.c }}>
                    {regimeCtxLoading && !regimeCtx ? "Gathering evidence…" : (v?.headline || "Insufficient historical evidence")}
                    {!(regimeCtxLoading && !regimeCtx) && v?.sample_count != null && (
                      <span className="font-normal opacity-75"
                        title={`n=${v.sample_count} regime-conditioned daily entries over 2y — the same canonical population the Regime-Ranked list scores against.`}
                        data-testid="evidence-verdict-n"> · n={v.sample_count}</span>
                    )}
                  </span>
                </div>
                {v?.verdict_text && (
                  <div className="text-[10px] mt-1" data-testid="evidence-verdict-text" style={{ color: "var(--hoip-text-secondary)" }}>
                    {v.verdict_text}
                  </div>
                )}
                {subtitle && (
                  <div className="text-[9px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {subtitle}
                  </div>
                )}
              </div>
            );
          })()}
          {/* P75 Sojourn DTE warning */}
          {regimeData?.regime?.sojourn_p75 && (() => {
            const currentDte = data?.active_dte || 30;
            const p75 = regimeData.regime.sojourn_p75;
            if (currentDte > p75) {
              return (
                <div className="mb-3 px-2 py-1.5 border rounded-sm text-[10px] flex items-center gap-2"
                  style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.05)", color: "#f59e0b" }}>
                  <AlertTriangle className="w-3 h-3" />
                  DTE ({currentDte}d) exceeds P75 regime sojourn ({p75}d) — consider shorter expiry to reduce regime-transition risk.
                </div>
              );
            }
            return null;
          })()}

          {rec.strategy && rec.strategy !== "no_trade" ? (
            <>
              {/* Strike Engine Mode Toggle (3-state): Δ Regime-Scaled / Key Levels / P(NT) */}
              <div className="flex items-center gap-1 mb-3 flex-wrap" data-testid="strike-mode-toggle">
                <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>Strike engine:</span>
                {STRIKE_MODE_OPTS.map(({ k, l }) => (
                  <button key={k} onClick={() => { setEngineMode(k); _writeStrikeMode(symbol, k); }} data-testid={`strike-mode-${k}`}
                    className={`px-2 py-0.5 text-[10px] border rounded-sm ${engineMode === k ? "bg-[var(--hoip-text-primary)] text-[var(--hoip-bg)] border-[var(--hoip-text-primary)]" : "border-[var(--hoip-border)] text-[var(--hoip-text-secondary)]"}`}>
                    {l}
                  </button>
                ))}
                {["bull_put_spread", "cash_secured_put", "short_put", "covered_call"].includes(rec.strategy) && data?.wing_scenarios?.engine_threshold && (
                  <span className="text-[10px] ml-1 font-mono-data" data-testid="strike-engine-method" style={{ color: "var(--hoip-text-tertiary)" }}>
                    → {data.wing_scenarios.engine_threshold}
                  </span>
                )}
                {data?.wing_scenarios?.engine_warning && (
                  <span className="text-[10px] ml-1 font-mono-data" data-testid="strike-engine-warning" style={{ color: "#f59e0b" }}>
                    · {data.wing_scenarios.engine_warning}
                  </span>
                )}
              </div>

              {(rec.short_rationale || rec.long_rationale) && (
                <div className="text-[10px] px-2 py-1.5 mb-2 border rounded-sm space-y-0.5" style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}>
                  {rec.short_rationale && <div>Short: {rec.short_rationale}</div>}
                  {rec.long_rationale && <div>Long: {rec.long_rationale}</div>}
                </div>
              )}

              <StrategyCard rec={rec} wingScenarios={data?.wing_scenarios} keyLevels={data?.key_levels}
                chainExpiry={options?.selected_expiry || selectedExpiry}
                selectedWing={selectedWing} setSelectedWing={setSelectedWing}
                selectedCallWing={selectedCallWing} setSelectedCallWing={setSelectedCallWing}
                selectedBcsShort={selectedBcsShort} setSelectedBcsShort={setSelectedBcsShort}
                selectedBcsLong={selectedBcsLong} setSelectedBcsLong={setSelectedBcsLong}
                backtestValidation={data?.backtest_validation}
                spot={sig.current_price}
                regimeData={regimeData}
                ivTermData={ivTermData}
                snappedExpiry={snappedExpiry}
                symbol={symbol}
                activeDte={data?.active_dte}
                techScore={Math.round(((sig.technical_score ?? 5) + (fund.fundamental_score ?? 5)) / 2 * 10)}
                regimeScore={regimeData?.score}
                combinedScore={regimeData?.score != null ? (regimeData.score < 30 ? regimeData.score : Math.round(Math.round(((sig.technical_score ?? 5) + (fund.fundamental_score ?? 5)) / 2 * 10) * 0.5 + regimeData.score * 0.5)) : null}
                user={user} />

              {/* Regime Backtest Context — historical performance of THIS strategy on
                  THIS ticker per regime (data-derived verdict; replaces binary Regime Fit). */}
              <RegimeBacktestContext symbol={symbol} strategy={rec.strategy}
                dte={data?.active_dte} regimeData={regimeData}
                ctx={regimeCtx} loadingProp={regimeCtxLoading} />

              {/* Historical Regime Edge — additive collapsible panel from the
                  Strike-Selection Lab cache (current regime row highlighted).
                  Iter143: gated to Pro/admin per user's Iter143 spec. */}
              {(user?.role === "admin" || user?.tier === "pro" || user?.tier === "legacy_pro") && (
                <>
                  <RegimeEdgePanel ticker={symbol} currentRegime={regimeData?.regime?.state} />
                  <RegimeEdgeChipsForTicker ticker={symbol} />
                </>
              )}



              {strategyReady && (
              <button data-testid="execute-trade-btn" disabled={executingTrade}
                onClick={async () => {
                  setExecutingTrade(true);
                  try {
                    await api.post("/pie/execute-trade", {
                      ticker: symbol, strategy: rec.strategy,
                      short_strike: rec.strikes?.short || rec.strikes?.call_short || rec.strikes?.put_short,
                      long_strike: rec.strikes?.long || rec.strikes?.call_long || rec.strikes?.put_long,
                      premium_collected: 0, contracts: 1, dte: data?.active_dte || 35,
                    });
                  } catch (e) { console.warn(e); }
                  finally { setTimeout(() => setExecutingTrade(false), 1500); }
                }}
                className={`mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 text-xs border rounded-sm transition-colors ${
                  executingTrade ? "bg-emerald-900/20 border-emerald-700 text-emerald-400" : "border-[var(--hoip-border)] text-[var(--hoip-text-secondary)] hover:text-[var(--hoip-text-primary)] hover:border-[var(--hoip-text-secondary)]"
                }`}>
                {executingTrade ? <><CheckCircle className="w-3 h-3" /> Logged to Trade Journal</> : <><Play className="w-3 h-3" /> Log Trade</>}
              </button>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>{rec.reason || "No trade recommended."}</p>
              {data?.key_levels && <KeyLevelsSection levels={data.key_levels} />}
            </div>
          )}
      </div>

      {/* Payoff + IV History */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        {strategyReady && payoffData?.payoff_data?.length > 0 && (
          <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            <h2 className="text-sm font-bold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>Payoff — {rec.strategy?.replace(/_/g, " ")}</h2>
            {(payoffData.input_quality_label || data?.wing_scenarios?.input_quality_label) && (() => {
              const label = payoffData.input_quality_label || data?.wing_scenarios?.input_quality_label;
              const tier = statusSeverity(label, { status: data?.option_freshness?.status, quote_complete: optionReadiness.quote_complete });
              return (
                <div className="mb-1" data-testid="payoff-input-quality">
                  <StatusBadge tier={tier} text={compactStatusLabel(label)} title={label} />
                </div>
              );
            })()}
            <div className="flex gap-4 mb-2 text-xs font-mono-data flex-wrap">
              <span>Max Profit: <span className="text-emerald-500">{payoffData.max_profit_label || `$${payoffData.max_profit?.toLocaleString()}`}</span></span>
              <span>Max Loss: <span className="text-red-500">{payoffData.max_loss_label || `$${payoffData.max_loss?.toLocaleString()}`}</span></span>
              {payoffData.breakeven_prices?.map((be, i) => (
                <span key={i}>B/E{payoffData.breakeven_prices.length > 1 ? ` ${i === 0 ? "Down" : "Up"}` : ""}: <span className="text-yellow-500">${be.toFixed(2)}</span></span>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={220} data-testid="payoff-chart">
              <AreaChart data={payoffData.payoff_data}>
                <defs>
                  <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="lossGrad" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                <XAxis dataKey="price" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={v => `$${v}`} interval={Math.floor(payoffData.payoff_data.length / 8)} />
                <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }} formatter={v => [`$${v.toFixed(2)}`, "P&L"]} labelFormatter={v => `Price: $${v}`} />
                <ReferenceLine y={0} stroke="var(--hoip-text-tertiary)" strokeWidth={1} />
                <ReferenceLine x={payoffData.current_price} stroke="#3B82F6" strokeDasharray="5 5" label={{ value: "Spot", position: "top", fill: "#3B82F6", fontSize: 8 }} />
                {payoffData.strike_markers?.map((m, i) => (
                  <ReferenceLine key={`s${i}`} x={m.strike} stroke="#71717A" strokeDasharray="3 3" label={{ value: `$${m.strike}`, position: "insideTopRight", fill: "#71717A", fontSize: 7 }} />
                ))}
                {payoffData.breakeven_prices?.map((be, i) => (
                  <ReferenceLine key={`be${i}`} x={be} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: `BE $${be.toFixed(0)}`, position: "insideBottomRight", fill: "#F59E0B", fontSize: 7 }} />
                ))}
                <Area type="monotone" dataKey="pnl" stroke="#10B981" fill="url(#profitGrad)" strokeWidth={2} dot={false}
                  baseValue={0} fillOpacity={1} isAnimationActive={false} />
                <Area type="monotone" dataKey={(d) => d.pnl < 0 ? d.pnl : 0} stroke="#EF4444" fill="url(#lossGrad)" strokeWidth={0} dot={false}
                  baseValue={0} fillOpacity={1} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        {ivHistory.length > 0 && (
          <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            <h2 className="text-sm font-bold mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>IV History</h2>
            <ResponsiveContainer width="100%" height={220} data-testid="iv-history-chart">
              <LineChart data={[...ivHistory].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                <XAxis dataKey="date" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={v => `${v}%`} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }} formatter={v => [`${v.toFixed(1)}%`, "ATM IV"]} />
                <Line type="monotone" dataKey="atm_iv" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 2, fill: "#8B5CF6" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* IV Term Structure — Collapsible */}
      {ivTermData?.term_structure?.length >= 2 && (
        <div className="border rounded-sm" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="iv-term-structure">
          <button onClick={() => setIvTermOpen(!ivTermOpen)} className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-bold cursor-pointer hover:bg-[var(--hoip-hover)]"
            style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }} data-testid="iv-term-toggle">
            <div className="flex items-center gap-2">
              <span>IV Term Structure</span>
              {ivTermData.signal_label && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold" style={{
                  color: ivTermData.signal_color, background: `${ivTermData.signal_color}15`
                }}>{ivTermData.signal_label}</span>
              )}
              {ivTermData.hv30 && <span className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>HV30: {ivTermData.hv30}%</span>}
            </div>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${ivTermOpen ? "rotate-180" : ""}`} style={{ color: "var(--hoip-text-tertiary)" }} />
          </button>
          {ivTermOpen && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{ivTermData.signal_desc}</p>
              <ResponsiveContainer width="100%" height={220} data-testid="iv-term-chart">
                <LineChart data={ivTermData.term_structure}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                  <XAxis dataKey="expiry" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }}
                    tickFormatter={v => v?.slice(5)} interval="preserveStartEnd" />
                  <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }}
                    tickFormatter={v => `${v}%`} domain={['auto', 'auto']} />
                  <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }}
                    formatter={(v, name) => [`${v?.toFixed(1)}%`, name === "atm_iv" ? "ATM IV" : "HV30"]}
                    labelFormatter={l => `Expiry: ${l}`} />
                  {ivTermData.hv30 && <ReferenceLine y={ivTermData.hv30} stroke="#F59E0B" strokeDasharray="5 5" label={{ value: `HV30 ${ivTermData.hv30}%`, position: "right", fill: "#F59E0B", fontSize: 9 }} />}
                  {ivTermData.earnings_date && (() => {
                    const ed = ivTermData.term_structure.find(t => t.is_earnings_expiry);
                    return ed ? <ReferenceLine x={ed.expiry} stroke="#F59E0B" strokeDasharray="5 5" label={{ value: "Earn", position: "top", fill: "#F59E0B", fontSize: 9 }} /> : null;
                  })()}
                  <Line type="monotone" dataKey="atm_iv" stroke="#8B5CF6" strokeWidth={2.5} dot={(props) => {
                    const { cx, cy, payload } = props;
                    return payload.is_earnings_expiry
                      ? <circle cx={cx} cy={cy} r={5} fill="#F59E0B" stroke="#F59E0B" />
                      : <circle cx={cx} cy={cy} r={3} fill="#8B5CF6" stroke="#8B5CF6" />;
                  }} />
                </LineChart>
              </ResponsiveContainer>
              {ivTermData.dte_note && (
                <div className="text-[10px] px-2 py-1.5 border rounded-sm" style={{ borderColor: "rgba(59,130,246,0.3)", background: "rgba(59,130,246,0.05)", color: "#3B82F6" }}>
                  {ivTermData.dte_note}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Volatility Skew — Collapsible */}
      {skewData?.skew_data?.length >= 3 && (
        <div className="border rounded-sm" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="vol-skew-panel">
          <button onClick={() => setSkewOpen(!skewOpen)} className="w-full px-4 py-2.5 flex items-center justify-between text-xs font-bold cursor-pointer hover:bg-[var(--hoip-hover)]"
            style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }} data-testid="vol-skew-toggle">
            <div className="flex items-center gap-2">
              <span>Volatility Skew</span>
              {skewData.signal_label && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold" style={{
                  color: skewData.signal_color, background: `${skewData.signal_color}15`
                }}>{skewData.signal_label}</span>
              )}
            </div>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${skewOpen ? "rotate-180" : ""}`} style={{ color: "var(--hoip-text-tertiary)" }} />
          </button>
          {skewOpen && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>{skewData.signal_desc}</p>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={skewData.skew_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hoip-divider)" />
                  <XAxis dataKey="strike" stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }}
                    tickFormatter={v => `$${v}`} interval="preserveStartEnd" />
                  <YAxis stroke="var(--hoip-text-tertiary)" tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }}
                    tickFormatter={v => `${v}%`} domain={['auto', 'auto']} />
                  <Tooltip contentStyle={{ background: "var(--hoip-card)", border: "1px solid var(--hoip-border)", fontSize: "10px", fontFamily: "JetBrains Mono" }}
                    formatter={(v, name) => [`${v?.toFixed(1)}%`, name === "call_iv" ? "Call IV" : "Put IV"]}
                    labelFormatter={l => `Strike: $${l}`} />
                  <ReferenceLine x={skewData.current_price} stroke="rgba(255,255,255,0.4)" strokeDasharray="5 5" label={{ value: "ATM", position: "top", fill: "rgba(255,255,255,0.5)", fontSize: 9 }} />
                  <Line type="monotone" dataKey="call_iv" stroke="#3B82F6" strokeWidth={2} dot={{ r: 2, fill: "#3B82F6" }} connectNulls />
                  <Line type="monotone" dataKey="put_iv" stroke="#EF4444" strokeWidth={2} dot={{ r: 2, fill: "#EF4444" }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Put Skew", value: `${skewData.put_skew > 0 ? "+" : ""}${skewData.put_skew} IV pts`, color: skewData.put_skew > 5 ? "#EF4444" : "var(--hoip-text-primary)" },
                  { label: "Call Skew", value: `${skewData.call_skew > 0 ? "+" : ""}${skewData.call_skew} IV pts`, color: skewData.call_skew > 3 ? "#10B981" : "var(--hoip-text-primary)" },
                  { label: "Skew Ratio", value: skewData.skew_ratio?.toFixed(2), color: skewData.skew_ratio > 1.5 ? "#F59E0B" : "var(--hoip-text-primary)" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="border p-2 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
                    <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{label}</div>
                    <div className="font-mono-data text-xs font-medium" style={{ color }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {user?.role !== "admin" && (
        <span className="sr-only" data-testid="option-snapshot-status">
          {options?.user_status
            || (options?.cache_status === "preparing" || !(options?.expirations || []).length
              ? "Option liquidity data unavailable"
              : (data?.option_freshness?.banner
                || data?.option_freshness?.user_label
                || `Option snapshot fetched ${data?.option_freshness?.fetched_at_et || options?.fetched_at || "—"}`))}
        </span>
      )}

      {/* Options Chain — Admin diagnostics only */}
      {user?.role === "admin" && options?.calls?.length > 0 && (
        <div className="border" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
          <div className="p-4 border-b" style={{ borderColor: "var(--hoip-border)" }}>
            <h2 className="text-sm font-bold inline" style={{ fontFamily: "Chivo, sans-serif" }}>Admin / Data Diagnostics — raw chain {options.selected_expiry || selectedExpiry}</h2>
            {options.atm_iv && <span className="ml-2 text-xs font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>ATM IV: {options.atm_iv?.toFixed(1)}%</span>}
            {(data?.available_expirations || options.expirations)?.length > 1 && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {(data?.available_expirations || options.expirations || []).slice(0, 8).map(raw => {
                  const exp = typeof raw === 'string' ? raw : raw.date;
                  const isMonthly = typeof raw === 'object' && raw.monthly;
                  return (
                  <button key={exp} onClick={async () => { setSelectedExpiry(exp); try { const { data: n } = await api.get(`/ticker/${symbol}/options?expiry=${exp}`); setOptions(n); } catch { /* ignore */ } }}
                    className={`text-xs px-2 py-1 border rounded-sm transition-colors ${(options.selected_expiry || selectedExpiry) === exp ? "bg-[var(--hoip-hover)] border-[var(--hoip-border-focus)] text-[var(--hoip-text-primary)]" : "border-[var(--hoip-border)] text-[var(--hoip-text-tertiary)] hover:text-[var(--hoip-text-primary)]"}`}
                    data-testid={`expiry-${exp}`}>{exp}{isMonthly ? " (M)" : ""}</button>
                  );
                })}
              </div>
            )}
          </div>
          {/* Desktop: Straddle view (calls | strike | puts) */}
          <div className="hidden md:block overflow-x-auto max-h-80" data-testid="straddle-chain">
            <table className="w-full dense-table">
              <thead className="sticky top-0 z-10" style={{ background: "var(--hoip-card)" }}>
                <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                  {["C IV","C OI","C Ask","C Bid"].map(h => <th key={h} className="text-right text-[10px] font-medium px-1.5" style={{ color: "rgba(16,185,129,0.6)" }}>{h}</th>)}
                  <th className="text-center text-xs font-bold px-2" style={{ color: "var(--hoip-text-primary)" }}>Strike</th>
                  {["P Bid","P Ask","P OI","P IV"].map(h => <th key={h} className="text-right text-[10px] font-medium px-1.5" style={{ color: "rgba(239,68,68,0.6)" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const calls = options.calls || [];
                  const puts = options.puts || [];
                  const strikeMap = {};
                  calls.forEach(c => { strikeMap[c.strike] = { ...(strikeMap[c.strike] || {}), call: c }; });
                  puts.forEach(p => { strikeMap[p.strike] = { ...(strikeMap[p.strike] || {}), put: p }; });
                  const allStrikes = Object.keys(strikeMap).map(Number).sort((a,b) => a-b);
                  const curPrice = options?.current_price || sig.current_price || 0;
                  const strikeValues = Object.values(rec.strikes || {}).filter(v => typeof v === "number");
                  let atmDone = false;
                  // Iter191b — center the visible window on spot (was slice(0,50) from
                  // deep ITM) and always include UOA-flagged strikes so jump targets exist.
                  const isElevated = (o) => o.volume >= 100 && o.open_interest > 0 && o.volume / o.open_interest >= 5;
                  const flaggedStrikes = allStrikes.filter(k =>
                    isElevated(strikeMap[k]?.call || {}) || isElevated(strikeMap[k]?.put || {}));
                  const windowStrikes = curPrice > 0
                    ? [...allStrikes].sort((a, b) => Math.abs(a - curPrice) - Math.abs(b - curPrice)).slice(0, 50)
                    : allStrikes.slice(0, 50);
                  const visibleStrikes = Array.from(new Set([...windowStrikes, ...flaggedStrikes])).sort((a, b) => a - b);
                  return visibleStrikes.map(strike => {
                    const c = strikeMap[strike]?.call || {};
                    const p = strikeMap[strike]?.put || {};
                    const isStratStrike = strikeValues.some(sv => Math.abs(sv - strike) < 0.5);
                    const isAtm = !atmDone && strike >= curPrice;
                    if (isAtm) atmDone = true;
                    // Iter191b — per-row Vol/OI elevation (matches UOA panel thresholds)
                    const cRatio = (c.volume >= 100 && c.open_interest > 0 && c.volume / c.open_interest >= 5) ? c.volume / c.open_interest : null;
                    const pRatio = (p.volume >= 100 && p.open_interest > 0 && p.volume / p.open_interest >= 5) ? p.volume / p.open_interest : null;
                    const uoaFlagged = cRatio != null || pRatio != null;
                    const isPulse = pulseStrike != null && Math.abs(Number(pulseStrike) - strike) < 0.01;
                    return (
                      <tr key={strike}
                        id={`chain-row-${strike}`}
                        data-testid={`chain-row-${strike}`}
                        className="border-b hover:bg-[var(--hoip-hover)]"
                        style={{
                          borderColor: "var(--hoip-divider)",
                          borderTop: isAtm ? "2px solid var(--hoip-text-tertiary)" : undefined,
                          borderLeft: uoaFlagged ? "3px solid #F59E0B" : undefined,
                          background: isPulse ? "rgba(245,158,11,0.28)" : uoaFlagged ? "rgba(245,158,11,0.05)" : undefined,
                          transition: "background-color 0.7s ease",
                        }}>
                        <td className="text-right font-mono-data text-[10px] px-1.5" style={isStratStrike ? { color: "#10B981" } : {}}>{c.implied_volatility ? `${c.implied_volatility?.toFixed(0)}%` : ""}</td>
                        <td className="text-right font-mono-data text-[10px] px-1.5 whitespace-nowrap">
                          {(c.open_interest || 0).toLocaleString()}
                          {cRatio != null && (
                            <sup className="ml-0.5 text-[8px] font-bold" style={{ color: "#F59E0B" }}
                              data-testid={`chain-voloi-call-${strike}`}
                              title={`Call volume ${(c.volume || 0).toLocaleString()} = ${cRatio.toFixed(1)}× open interest — elevated new-positioning proxy (UOA)`}>{cRatio.toFixed(1)}×</sup>
                          )}
                        </td>
                        <td className="text-right font-mono-data text-[10px] px-1.5">{c.ask?.toFixed(2) || ""}</td>
                        <td className="text-right font-mono-data text-[10px] px-1.5">{c.bid?.toFixed(2) || ""}</td>
                        <td className="text-center font-mono-data text-xs font-bold px-2" style={{
                          background: isStratStrike ? "rgba(16,185,129,0.12)" : c.in_the_money ? "rgba(16,185,129,0.04)" : undefined,
                          borderLeft: isStratStrike ? "3px solid #10B981" : undefined,
                          borderRight: isStratStrike ? "3px solid #EF4444" : undefined,
                        }}>${strike.toFixed(1)}</td>
                        <td className="text-right font-mono-data text-[10px] px-1.5">{p.bid?.toFixed(2) || ""}</td>
                        <td className="text-right font-mono-data text-[10px] px-1.5">{p.ask?.toFixed(2) || ""}</td>
                        <td className="text-right font-mono-data text-[10px] px-1.5 whitespace-nowrap">
                          {(p.open_interest || 0).toLocaleString()}
                          {pRatio != null && (
                            <sup className="ml-0.5 text-[8px] font-bold" style={{ color: "#F59E0B" }}
                              data-testid={`chain-voloi-put-${strike}`}
                              title={`Put volume ${(p.volume || 0).toLocaleString()} = ${pRatio.toFixed(1)}× open interest — elevated new-positioning proxy (UOA)`}>{pRatio.toFixed(1)}×</sup>
                          )}
                        </td>
                        <td className="text-right font-mono-data text-[10px] px-1.5" style={isStratStrike ? { color: "#EF4444" } : {}}>{p.implied_volatility ? `${p.implied_volatility?.toFixed(0)}%` : ""}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          {/* Mobile: Tabs fallback */}
          <div className="md:hidden">
            <Tabs defaultValue="calls">
              <TabsList className="bg-[var(--hoip-card)] mx-4 mt-2 border border-[var(--hoip-border)] rounded-sm h-8">
                <TabsTrigger value="calls" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]">Calls ({options.calls?.length})</TabsTrigger>
                <TabsTrigger value="puts" className="text-xs rounded-sm data-[state=active]:bg-[var(--hoip-hover)]">Puts ({options.puts?.length})</TabsTrigger>
              </TabsList>
              {["calls", "puts"].map(type => (
                <TabsContent key={type} value={type} className="mt-0">
                  <div className="overflow-x-auto max-h-72">
                    <table className="w-full dense-table">
                      <thead className="sticky top-0 z-10" style={{ background: "var(--hoip-card)" }}>
                        <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                          {["Strike","Bid","Ask","Last","Vol","OI","IV"].map(h => (
                            <th key={h} className="text-right text-xs font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(type === "calls" ? options.calls : options.puts)?.slice(0, 40).map((o, i) => {
                          const r = (o.volume >= 100 && o.open_interest > 0 && o.volume / o.open_interest >= 5) ? o.volume / o.open_interest : null;
                          return (
                          <tr key={i} className="border-b hover:bg-[var(--hoip-hover)]" style={{ borderColor: "var(--hoip-divider)", borderLeft: r != null ? "3px solid #F59E0B" : undefined, background: r != null ? "rgba(245,158,11,0.05)" : undefined }}>
                            <td className="text-right font-mono-data text-xs font-medium">${o.strike?.toFixed(1)}</td>
                            <td className="text-right font-mono-data text-xs">{o.bid?.toFixed(2)}</td>
                            <td className="text-right font-mono-data text-xs">{o.ask?.toFixed(2)}</td>
                            <td className="text-right font-mono-data text-xs">{o.last_price?.toFixed(2)}</td>
                            <td className="text-right font-mono-data text-xs">{(o.volume || 0).toLocaleString()}</td>
                            <td className="text-right font-mono-data text-xs whitespace-nowrap">
                              {(o.open_interest || 0).toLocaleString()}
                              {r != null && <sup className="ml-0.5 text-[8px] font-bold" style={{ color: "#F59E0B" }}>{r.toFixed(1)}×</sup>}
                            </td>
                            <td className="text-right font-mono-data text-xs">{o.implied_volatility?.toFixed(1)}%</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      )}

      {/* P1.9 — Order Ticket dialog */}
      <OrderTicketDialog
        open={!!orderTicket}
        onClose={() => setOrderTicket(null)}
        ticket={orderTicket}
      />
    </div>
  );
}
